import { Injectable, OnDestroy } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpBackend,
} from '@angular/common/http';
import { Observable, throwError, forkJoin, of, Subscription, Subject } from 'rxjs';
import {
  catchError,
  map,
  tap,
  withLatestFrom,
  switchMap,
  take,
  takeUntil,
  timeout,
  distinctUntilChanged,
  filter,
  shareReplay,
} from 'rxjs/operators';
import { select, Store } from '@ngrx/store';



import { environment } from 'src/environments/environment';
import { CatalogResponse, CatalogTypeMapper } from '../models/catalog.model';
import { CatalogService } from './catalog.service';
import { VEHICLE_SIGNALS_ENDPOINT } from '../config/config.constants';

import { AppStateInterface } from 'src/app/appState.interface';
import { getDriversOfCustomersSelector, selectVehicleFeature, getApplySelectedCustomer } from '../store/selectors';
import * as IotAssetActions from '../store/iotAssets/iotAsset.actions';
import { selectMappingConfig } from '../store/iotAssets/iotAsset.selector';

import { CurrentUserInfo } from '../models/currentUserInfo';
import { DataService } from './data.service';
import { IotAsset, IotAssetsApiResponse, IotAssetRawData, IotAssetScope, IotAssetAttribute, AdditionalSignalsConfig, AdditionalSignalsScope, MappingConfig, UControlSignalsConfig } from '../models/iotAssetsModel.data';
import { IotAssetState } from '../store/iotAssets/iotAsset.reducer';
import { SignalCatalogItem, SignalCatalogResponse } from '../models/signals.model';
import { AssetFieldConfig, AssetMappingConfig, buildFieldConfigKey, getFieldConfig, findFieldConfigByName, extractNameFromFieldConfigKey } from '../models/asset-config.model';
import { MAP_FIELD_SCOPES, MapFieldScope } from '../config/config.constants';




@Injectable({
  providedIn: 'root',
})
export class IotAssetsService implements OnDestroy {
  public apiUrl = environment.baseTsOhwUrl;
  private modifiedCoordinates: { [key: string]: { lat: number; lon: number } } =
    {};
    private httpWithoutInterceptor: HttpClient;
    driversOfCustomer: any[] = [];
    iotAssetsOfCustomer: any[] = [];
    private destroy$ = new Subject<void>();

    // 🔧 Subject per cancellare operazioni quando cambia customer
    private customerChange$ = new Subject<void>();

    // Subject per notificare aggiornamenti degli indirizzi
    private addressUpdated$ = new Subject<IotAsset[]>();
    public addressUpdatedObservable$ = this.addressUpdated$.asObservable();

    // Subject per notificare aggiornamenti della configurazione mappa
    private mappingConfigUpdated$ = new Subject<AssetMappingConfig>();
    public mappingConfigUpdatedObservable$ = this.mappingConfigUpdated$.asObservable();

    // Configurazione dinamica
    private mappingConfig: AssetMappingConfig | null = null;
    private configLoaded = false;
    private configLoading = false; // 🔧 Flag per evitare caricamenti concorrenti
    private lastProcessedCustomerUid: string | null = null; // 🔧 Track dell'ultimo customer processato

    // 🔧 Track per evitare chiamate duplicate a loadCatalogConfiguration
    private catalogConfigRequestsInProgress = new Map<string, Observable<{ data: any, audience?: 'USER' | 'CUSTOMER' | 'GLOBAL' }>>();

    // 🔧 Cache per configurazioni API per evitare chiamate multiple
    private apiConfigCache: Map<string, { data: MappingConfig[]; timestamp: number }> = new Map();
    private readonly API_CACHE_TTL = 30000; // Cache valida per 30 secondi

    // Rate limiting per OpenStreetMap Nominatim (max 1 richiesta/secondo)
    private geocodingQueue: Array<{
      lat: number;
      lon: number;
      observer: any;
    }> = [];
    private lastGeocodingRequest = 0;
    private readonly GEOCODING_DELAY = 1100; // 1.1 secondi per essere sicuri

    // Gestione cache geocoding
    private readonly GEOCACHE_PREFIX = 'geocache_';
    private readonly MAX_CACHE_ITEMS = 500; // Massimo 500 indirizzi in cache
    private readonly CACHE_TTL = 24 * 60 * 60 * 1000; // 24 ore
  constructor(
    private http: HttpClient,
    private httpBackend: HttpBackend,
    private store: Store<{ iotAssets: IotAssetState }>,
    private storeAnag: Store<AppStateInterface>,
    private dataService: DataService,
    private catalogService: CatalogService,
    // private geocodingService: GeocodingService
  ) {
      // Crea un'istanza di HttpClient che bypassa gli interceptor
      this.httpWithoutInterceptor = new HttpClient(this.httpBackend);

      // NON caricare automaticamente - solo quando richiesto
      // this.loadMappingConfiguration();

      // Pulisci automaticamente la cache geocoding scaduta all'avvio
      this.cleanExpiredGeocodingCache();

      // Esponi il servizio per debug console (solo in development)
      if (!environment.production) {
        (window as any).iotAssetsService = this;
        // console.log('🛠️ Servizio IoT Assets esposto per debug: window.iotAssetsService');
        // console.log('💡 Comandi disponibili:');
        // console.log('   - window.iotAssetsService.checkConfigStatus() - Verifica stato configurazione');
        // console.log('   - window.iotAssetsService.forceSyncConfiguration() - Forza sincronizzazione');
        // console.log('   - window.iotAssetsService.clearApiCache() - Pulisce cache API');
        // console.log('   - window.iotAssetsService.cancelPendingOperations() - Cancella operazioni in corso');
      }

    // Usa takeUntil per gestire automaticamente l'unsubscribe
    this.storeAnag.pipe(
      select(selectVehicleFeature),
      takeUntil(this.destroy$)
    ).subscribe((resVehicle) => {
      if (resVehicle !== undefined && resVehicle !== null) {
        this.iotAssetsOfCustomer = JSON.parse(JSON.stringify(resVehicle));
      } else {
        this.iotAssetsOfCustomer = [];
      }
     // console.log('iotAssetsOfCustomers$', this.iotAssetsOfCustomer);
    });

    this.storeAnag.pipe(
      select(getDriversOfCustomersSelector),
      takeUntil(this.destroy$)
    ).subscribe((resDrivers) => {
      this.driversOfCustomer = resDrivers;
   //   console.log('selectedAssetsDrivers', this.driversOfCustomer);
    });
  }

  // Metodi per la configurazione dinamica

  /**
   * Inizializza il servizio con caricamento configurazione
   * Deve essere chiamato esplicitamente dai componenti che lo utilizzano
   */
  public initializeService(): void {
    // 🔧 Previeni inizializzazioni multiple concorrenti
    if (this.configLoading) {
      // console.log('⏳ Inizializzazione già in corso, attendo completamento...');
      return;
    }

    if (!this.configLoaded && !this.mappingConfig) {
      // console.log('🚀 Inizializzazione IotAssetsService...');
      this.loadMappingFromAPI();
    } else {
      // console.log('✅ IotAssetsService già inizializzato');
    }
  }

  /**
   * Metodo per gestire il cambio customer
   * Ricarica la configurazione mapping per il nuovo customer
   * @param newCustomerUid UID del nuovo customer
   */
  public handleCustomerChange(newCustomerUid: string): void {
    // console.log('🔄 Cambio customer rilevato, ricaricamento configurazione mapping per:', newCustomerUid);

    // 🔧 CANCELLA TUTTE le operazioni in corso per il customer precedente
    this.customerChange$.next();
    // console.log('🛑 Operazioni del customer precedente cancellate');

    // Reset dello stato di configurazione
    this.configLoaded = false;
    this.configLoading = false; // 🔧 Reset flag caricamento
    this.mappingConfig = null;
    this.lastProcessedCustomerUid = null; // 🔧 Reset ultimo customer processato
    this.catalogConfigRequestsInProgress.clear(); // 🔧 Reset catalog config requests

    // 🔧 Pulisci la cache API per forzare il reload
    this.clearApiConfigCache();

    // Ricarica la configurazione per il nuovo customer utilizzando solo il catalogo
    this.loadMappingFromAPI();
  }

  /**
   * Pulisce la cache delle configurazioni API
   */
  private clearApiConfigCache(): void {
    this.apiConfigCache.clear();
    // console.log('🧹 Cache configurazioni API pulita');
  }

  /**
   * Invalida la cache per un customer specifico (journey config)
   * @param uidCustomer UID del customer
   */
  public invalidateJourneyCacheForCustomer(uidCustomer: string): void {
    const cacheKey = `journey_${uidCustomer}`;
    this.apiConfigCache.delete(cacheKey);
    console.log('🗑️ Cache journey invalidata manualmente per customer:', uidCustomer);
  }

  public invalidateCatalogCacheForCustomer(uidCustomer: string): void {
    // Invalida cache per tutte le audience (USER, CUSTOMER, GLOBAL) - config API
    const audiences = ['USER', 'CUSTOMER', 'GLOBAL'];
    audiences.forEach(audience => {
      const cacheKey = `catalog_${uidCustomer}_${audience}`;
      this.apiConfigCache.delete(cacheKey);
    });
    console.log('🗑️ Cache catalog invalidata manualmente per customer:', uidCustomer);
  }

  // private loadMappingConfiguration(): void {
  //   const configPath = '/assets/config/vehicle-mapping-config.json';
  //   const storageKey = 'vehicle-mapping-config';

  //   // Ottieni le informazioni dell'utente corrente
  //   const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
  //   const uidCustomer = userInfo.user?.uidCustomer;
  //   const uidUser = userInfo.user?.uid;

  //   console.log('📋 Caricamento configurazione mapping per customer:', uidCustomer);

  //   // Prima prova a caricare dalla API se abbiamo le credenziali utente
  //   if (uidCustomer && uidUser) {
  //     this.loadMappingFromAPI(uidCustomer, uidUser, configPath, storageKey);
  //   } else {
  //     console.warn('Credenziali utente non disponibili, carico configurazione da localStorage/file');
  //     this.loadMappingFromLocalStorage(configPath, storageKey);
  //   }
  // }

  private loadMappingFromAPI(): void {
    // Ottieni le informazioni del customer dal store NgRx e l'UID utente da DataService
    this.storeAnag.pipe(
      select(getApplySelectedCustomer),
      distinctUntilChanged((prev, curr) => {
        // Confronta gli UID dei customer per evitare chiamate duplicate
        const prevUid = prev && typeof prev === 'object' && 'uid' in prev ? (prev as any).uid : null;
        const currUid = curr && typeof curr === 'object' && 'uid' in curr ? (curr as any).uid : null;
        return prevUid === currUid;
      }),
      takeUntil(this.destroy$)
    ).subscribe((selectedCustomer) => {
      const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
      const uidCustomer = selectedCustomer && typeof selectedCustomer === 'object' && 'uid' in selectedCustomer
        ? (selectedCustomer as any).uid
        : null;
      const uidUser = userInfo.user?.uid;

      if (!uidCustomer || !uidUser) {
        console.error('❌ Impossibile caricare configurazione: credenziali mancanti', {
          uidCustomer,
          uidUser: !!uidUser
        });
        this.mappingConfig = this.getFallbackMappingConfig();
        this.configLoaded = true;
        this.configLoading = false;
        return;
      }

      // 🔧 Evita caricamenti concorrenti per lo stesso customer
      if (this.configLoading) {
        // console.log('⏳ Caricamento già in corso, salto richiesta per customer:', uidCustomer);
        return;
      }

      // 🔧 Verifica se questo customer è già stato processato
      if (this.lastProcessedCustomerUid === uidCustomer && this.configLoaded && this.mappingConfig) {
        console.log('✅ Customer già processato, salto chiamata getCatalog per:', uidCustomer);
        return;
      }

      console.log('🔄 Caricamento configurazione mapping - Customer:', uidCustomer);
      this.configLoading = true;

      // Prima tenta di caricare la config salvata su API direttamente (senza catalog sync)
      this.catalogService.getMappingConfigFromAPI(uidCustomer, uidUser).pipe(
        takeUntil(this.customerChange$),
        takeUntil(this.destroy$)
      ).subscribe({
        next: (configs) => {
          const apiConfig = configs?.[0];
          if (apiConfig?.config?.mappingData) {
            console.log('✅ Configurazione mapping caricata direttamente da API');
            this.mappingConfig = { ...apiConfig.config.mappingData, audience: apiConfig.audience ?? this.mappingConfig?.audience };
            this.configLoaded = true;
            this.configLoading = false;
            this.lastProcessedCustomerUid = uidCustomer;
            this.updateNgRxStore();
          } else {
            console.log('📭 Nessuna config salvata trovata, costruisco da catalogo');
            this.loadCatalogAndConfiguration(uidCustomer, uidUser);
          }
        },
        error: () => {
          console.warn('⚠️ Errore caricamento API config, fallback a catalogo');
          this.loadCatalogAndConfiguration(uidCustomer, uidUser);
        }
      });

    });
  }

  private loadCatalogAndConfiguration(uidCustomer: string, uidUser: string): void {
    console.log('📡 Caricamento catalogo per customer:', uidCustomer);
    this.catalogService.getCatalog().pipe(
      takeUntil(this.customerChange$),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (catalogResponse) => {
        console.log('📊 CATALOGO RICEVUTO - Struttura completa:', catalogResponse);
        // 🔧 Marca questo customer come processato
        this.lastProcessedCustomerUid = uidCustomer;

        // Converti il catalogo in configurazione mapping
        const catalogConfig = this.convertCatalogToMappingConfig(catalogResponse);

        // 🔍 LOG DETTAGLIATO: Configurazione generata dal catalogo
        console.log('📋 CONFIGURAZIONE DA CATALOGO:');
        console.log('  - Versione:', catalogConfig.version);
        // console.log('  - Campi totali:', Object.keys(catalogConfig.fields).length);
        // console.log('  - Campi comuni:', Object.values(catalogConfig.fields).filter(f => f.source === 'common').length);
        // console.log('  - Campi modelli:', Object.values(catalogConfig.fields).filter(f => f.source === 'model').length);
        // console.log('  - Primi 10 campi catalogo:', Object.keys(catalogConfig.fields).slice(0, 10));

        // Poi ottieni la configurazione dall'API
        this.catalogService.getMappingConfigFromAPI(uidCustomer, uidUser).pipe(
          takeUntil(this.customerChange$), // 🔧 Cancella se cambia customer
          takeUntil(this.destroy$) // 🔧 Cancella quando il servizio viene distrutto
        ).subscribe({
          next: (apiConfigs) => {
            const apiConfig = apiConfigs && apiConfigs.length > 0 ? apiConfigs[0] : null;
            const currentApiConfig = apiConfig?.config?.mappingData;
            const apiAudience = apiConfig?.audience;

            // Sincronizza le configurazioni (ora usa catalogConfig invece di jsonConfig)
            const syncedConfig = this.syncConfigurations(catalogConfig, currentApiConfig, apiConfig);
            console.log('📋 CONFIGURAZIONE DA API:', apiConfig);
            console.log('📋 CONFIGURAZIONE SINCRONIZZATA:', syncedConfig);

            // 🔍 LOG DETTAGLIATO: Risultato sincronizzazione
            // console.log('🔄 RISULTATO SINCRONIZZAZIONE:');
            // console.log('  - Ha modifiche:', syncedConfig.hasChanges);
            // console.log('  - Campi nella config sincronizzata:', Object.keys(syncedConfig.config.fields).length);
            // console.log('  - Versione config sincronizzata:', syncedConfig.config.version);
            // console.log('  - Config API era presente:', !!currentApiConfig);
            // if (currentApiConfig) {
            //   console.log('  - Campi in API config:', Object.keys(currentApiConfig.fields || {}).length);
            // }

            if (syncedConfig.hasChanges) {
              // console.log('🔄 Configurazioni sincronizzate, aggiorno API...');

              // 🔧 IMPORTANTE: Imposta IMMEDIATAMENTE la configurazione per i componenti
              this.mappingConfig = syncedConfig.config;
              this.configLoaded = true;
              this.configLoading = false; // 🔧 Reset flag
              this.updateNgRxStore();
              // console.log('✅ Configurazione disponibile immediatamente per i componenti');

              // Salva la configurazione sull'API in background (non bloccante)
              // this.saveMappingConfigToAPI(uidCustomer, uidUser, syncedConfig.config).pipe(
              //   takeUntil(this.customerChange$) // 🔧 Cancella se cambia customer
              // ).subscribe({
              //   next: () => {
              //     // console.log('✅ Configurazione sincronizzata salvata su API (background)');
              //   },
              //   error: (error) => {
              //     console.error('❌ Errore nel salvataggio background su API (non critico):', error);
              //   }
              // });
            } else {
              // console.log('✅ Configurazioni già sincronizzate, nessun aggiornamento necessario');
              if (currentApiConfig && apiAudience) {
                // Se esiste configurazione API con audience, aggiungila
                this.mappingConfig = {
                  ...currentApiConfig,
                  audience: apiAudience,
                  // idCustomer: uidCustomer
                };
              } else {
                // Usa configurazione da catalogo come fallback
                this.mappingConfig = catalogConfig;
              }
              this.configLoaded = true;
              this.configLoading = false; // 🔧 Reset flag
              //this.saveMappingConfigToStorage();
              this.updateNgRxStore();
            }
          },
          error: (error) => {
            console.error('Errore nel caricamento da API, uso configurazione da catalogo:', error);

            // 🔧 Usa la configurazione del catalogo immediatamente
            this.mappingConfig = catalogConfig;
            this.configLoaded = true;
            this.configLoading = false; // 🔧 Reset flag
            this.updateNgRxStore();
            console.log('✅ Configurazione da catalogo disponibile immediatamente dopo errore API');

            // Tenta di salvare la configurazione del catalogo per il futuro (background)
            // this.saveMappingConfigToAPI(uidCustomer, uidUser, catalogConfig).pipe(
            //   takeUntil(this.customerChange$) // 🔧 Cancella se cambia customer
            // ).subscribe({
            //   next: () => console.log('✅ Configurazione catalogo salvata su API (recovery)'),
            //   error: (saveError) => console.warn('⚠️ Impossibile salvare configurazione catalogo su API:', saveError)
            // });
          }
        });
      },
      error: (error) => {
        console.error('❌ Errore nel caricamento catalogo, uso configurazione fallback:', error);

        // 🔧 Usa la configurazione di fallback immediatamente
        this.mappingConfig = this.getFallbackMappingConfig();
        this.configLoaded = true;
        this.configLoading = false; // 🔧 Reset flag anche in caso di errore
        this.lastProcessedCustomerUid = uidCustomer; // 🔧 Marca anche in caso di errore per evitare retry continui
        this.updateNgRxStore();
        // console.log('✅ Configurazione fallback disponibile immediatamente dopo errore catalogo');
      }
    });
  }

  // private loadMappingFromLocalStorage(configPath: string, storageKey: string): void {
  //   // Prima prova a caricare dal localStorage
  //   const savedConfig = localStorage.getItem(storageKey);
  //   if (savedConfig) {
  //     try {
  //       const parsedConfig = JSON.parse(savedConfig) as AssetMappingConfig;
  //       this.mappingConfig = parsedConfig;
  //       this.configLoaded = true;
  //       console.log(`Configurazione mapping caricata da localStorage v${parsedConfig.version}:`, Object.keys(parsedConfig.fields).length, 'campi');

  //       // Carica comunque il file originale per eventuali aggiornamenti
  //       this.loadConfigFromFile(configPath, storageKey, parsedConfig);
  //       return;
  //     } catch (error) {
  //       console.warn('Errore nel parsing della configurazione salvata, carico da file:', error);
  //       localStorage.removeItem(storageKey);
  //     }
  //   }

  //   // Se non c'è configurazione salvata, carica dal file
  //   this.loadConfigFromFile(configPath, storageKey);
  // }

  // private loadConfigFromFile(configPath: string, storageKey: string, savedConfig?: AssetMappingConfig): void {
  //   this.http.get<AssetMappingConfig>(configPath)
  //     .pipe(
  //       catchError((error) => {
  //         console.error('Errore nel caricamento della configurazione mapping:', error);
  //         // Configurazione di fallback semplificata
  //         return of({
  //           version: "fallback",
  //           lastUpdated: new Date().toISOString(),
  //           description: "Configurazione di fallback",
  //           fields: {
  //             'uid': { type: 'string', label: 'UID', visible: true },
  //             'id_vehicle': { type: 'string', label: 'ID Asset', visible: true },
  //             'vin': { type: 'string', label: 'VIN', visible: true },
  //             'plate': { type: 'string', label: 'Identificativo', visible: true },
  //             'vehicle_status': { type: 'string', label: 'Stato Asset', visible: true }
  //           }
  //         } as AssetMappingConfig);
  //       })
  //     )
  //     .subscribe((fileConfig) => {
  //       // Se abbiamo una configurazione salvata, mantieni le impostazioni di visibilità dell'utente
  //       if (savedConfig) {
  //         // Merge: prendi i campi dal file ma mantieni le impostazioni di visibilità salvate
  //         const mergedFields: Record<string, AssetFieldConfig> = {};

  //         // Prima aggiungi tutti i campi dal file con le loro impostazioni di default
  //         Object.keys(fileConfig.fields).forEach(key => {
  //           mergedFields[key] = { ...fileConfig.fields[key] };
  //         });

  //         // Poi sovrascrivi con le impostazioni di visibilità salvate dall'utente
  //         Object.keys(savedConfig.fields).forEach(key => {
  //           if (mergedFields[key]) {
  //             mergedFields[key].visible = savedConfig.fields[key].visible;
  //           }
  //         });

  //         // Assicurati che il VIN sia sempre presente nel mapping, anche se non nel file config
  //         if (!mergedFields.vin) {
  //           mergedFields.vin = { type: 'string', label: 'VIN', visible: false };
  //         }

  //         this.mappingConfig = {
  //           ...fileConfig,
  //           fields: mergedFields,
  //           lastUpdated: new Date().toISOString()
  //         };
  //       } else {
  //         // Assicurati che il VIN sia sempre presente anche nella configurazione base
  //         if (!fileConfig.fields.vin) {
  //           fileConfig.fields.vin = { type: 'string', label: 'VIN', visible: false };
  //         }
  //         this.mappingConfig = fileConfig;
  //       }

  //       this.configLoaded = true;

  //       // Salva la configurazione aggiornata nel localStorage
  //       this.saveMappingConfigToStorage();

  //     //  console.log(`Configurazione mapping caricata v${this.mappingConfig.version}:`, Object.keys(this.mappingConfig.fields).length, 'campi');
  //     });
  // }

  // Nuovo metodo per salvare la configurazione nel localStorage E nelle API
  // private saveMappingConfigToStorage(): void {
  //   if (this.mappingConfig) {
  //     try {
  //       const storageKey = 'vehicle-mapping-config';
  //       // Salva nel localStorage come backup
  //       localStorage.setItem(storageKey, JSON.stringify(this.mappingConfig));
  //       console.log('Configurazione mapping salvata nel localStorage');

  //       // Salva anche tramite API se possibile
  //    //   this.saveMappingConfigToAPI();
  //     } catch (error) {
  //       console.error('Errore nel salvataggio della configurazione nel localStorage:', error);
  //     }
  //   }
  // }

  /**
   * Converte la risposta del catalogo in formato AssetMappingConfig
   * @param catalogResponse Risposta del catalogo API
   * @returns Configurazione mapping generata dal catalogo
   */
  private convertCatalogToMappingConfig(catalogResponse: CatalogResponse): AssetMappingConfig {
    // console.log('🔄 Conversione catalogo in configurazione mapping...');

    if (!catalogResponse?.data) {
      console.warn('⚠️ Catalogo vuoto o formato non valido, uso configurazione fallback');
      return this.getFallbackMappingConfig();
    }

    const fields: Record<string, AssetFieldConfig> = {};
    let totalAttributesCount = 0;

    // 1. Elabora gli attributi comuni (disponibili per tutti i veicoli)
    if (catalogResponse.data.commonAttributes && Array.isArray(catalogResponse.data.commonAttributes)) {
      const totalCommonAttrs = catalogResponse.data.commonAttributes.length;
      let filteredCommonAttrs = 0;

      catalogResponse.data.commonAttributes.forEach(attr => {
        if (!attr.name) return;

        // Filtra solo gli scope rilevanti per la mappa
        if (!MAP_FIELD_SCOPES.includes(attr.scope as MapFieldScope)) {
          return;
        }

        filteredCommonAttrs++;

        fields[buildFieldConfigKey(attr.scope, attr.name)] = {
          type: CatalogTypeMapper.mapType(attr.type),
          label: CatalogTypeMapper.getReadableLabel(attr.label, attr.name),
          visible: CatalogTypeMapper.getDefaultVisibility(attr.scope, attr.name),
          // Aggiungi metadati extra dal catalogo
          scope: attr.scope,
          unit: attr.unit || undefined,
          catalogType: attr.type,
          source: 'common', // Marca come attributo comune,
          uid: attr.uid || undefined // Se disponibile, altrimenti undefined
        };
        totalAttributesCount++;
      });
    }

    // 2. Elabora gli attributi specifici per modello
    if (catalogResponse.data.modelsAttributes && Array.isArray(catalogResponse.data.modelsAttributes)) {
      // console.log('🏭 Elaborazione attributi per modelli:', catalogResponse.data.modelsAttributes.length, 'modelli');

      const modelAttributesMap: Record<string, string[]> = {};

      catalogResponse.data.modelsAttributes.forEach(modelData => {
        if (!modelData.model_name || !modelData.attributes) return;

        const modelName = modelData.model_name;
        modelAttributesMap[modelName] = [];

        modelData.attributes.forEach(attr => {
          if (!attr.name) return;

          // Filtra solo gli scope rilevanti per la mappa
          if (!MAP_FIELD_SCOPES.includes(attr.scope as MapFieldScope)) {
            return;
          }

          // Se l'attributo non esiste già nei campi comuni, aggiungilo
          const modelFieldKey = buildFieldConfigKey(attr.scope, attr.name);
          if (!fields[modelFieldKey]) {
            fields[modelFieldKey] = {
              type: CatalogTypeMapper.mapType(attr.type),
              label: CatalogTypeMapper.getReadableLabel(attr.label, attr.name),
              visible: CatalogTypeMapper.getDefaultVisibility(attr.scope, attr.name),
              scope: attr.scope,
              unit: attr.unit || undefined,
              catalogType: attr.type,
              source: 'model', // Marca come attributo specifico del modello
              availableInModels: [modelName], // Lista dei modelli che supportano questo attributo
              uid: attr.uid || undefined // Se disponibile, altrimenti undefined
            };
            totalAttributesCount++;
          } else {
            // Se esiste già, aggiorna la lista dei modelli che lo supportano
            if (fields[modelFieldKey].source === 'model') {
              if (!fields[modelFieldKey].availableInModels) {
                fields[modelFieldKey].availableInModels = [];
              }
              if (!fields[modelFieldKey].availableInModels!.includes(modelName)) {
                fields[modelFieldKey].availableInModels!.push(modelName);
              }
            }
          }

          modelAttributesMap[modelName].push(attr.name);
        });
      });

      // Log degli attributi per modello
      Object.entries(modelAttributesMap).forEach(([modelName, attributes]) => {
        // console.log(`🔧 Modello ${modelName}: ${attributes.length} attributi specifici`);
      });
    }

    // console.log(`✅ Catalogo convertito: ${Object.keys(fields).length} campi totali trovati`);
    // console.log('📊 Statistiche:');
    // console.log('  - Attributi comuni:', Object.values(fields).filter(f => f.source === 'common').length);
    // console.log('  - Attributi specifici modello:', Object.values(fields).filter(f => f.source === 'model').length);
    // console.log('📋 Campi per scope:', this.getFieldsByScope(fields));
    console.log('🗺️ Filtro scope mappa applicato. Scope inclusi:', MAP_FIELD_SCOPES);
    console.log('📊 Attributi filtrati totali:', totalAttributesCount);

    const commonCount = catalogResponse.data.commonAttributes?.length || 0;
    const modelCount = catalogResponse.data.modelsAttributes?.length || 0;

    return {
      version: `catalog-${new Date().toISOString().split('T')[0]}`,
      lastUpdated: new Date().toISOString(),
      description: `Configurazione generata dal catalogo API - ${commonCount} attributi comuni + ${modelCount} modelli con attributi specifici`,
      audience: catalogResponse.audience || 'USER', // Legge da catalogResponse, fallback su 'USER'

      fields
    };
  }

  /**
   * Raggruppa i campi per scope per logging
   */
  private getFieldsByScope(fields: Record<string, AssetFieldConfig>): Record<string, number> {
    const scopeCount: Record<string, number> = {};
    Object.values(fields).forEach(field => {
      if (field.scope) {
        scopeCount[field.scope] = (scopeCount[field.scope] || 0) + 1;
      }
    });
    return scopeCount;
  }

  /**
   * Configurazione di fallback in caso di errori
   */
  private getFallbackMappingConfig(): AssetMappingConfig {
    return {
      version: "fallback",
      lastUpdated: new Date().toISOString(),
      description: "Configurazione di fallback",
      audience: 'USER',

      fields: {
        'vin': { type: 'string', label: 'VIN', visible: false },
        'vehicle_status': { type: 'string', label: 'Stato Veicolo', visible: true },
        'lat_current': { type: 'number', label: 'Latitudine', visible: true },
        'lon_current': { type: 'number', label: 'Longitudine', visible: true },
        'fuel': { type: 'number', label: 'Carburante', visible: true },
        'rpm': { type: 'number', label: 'RPM', visible: true }
      }
    };
  }

  // /**
  //  * Carica il file JSON di configurazione master
  //  */
  // private loadJsonConfigFile(configPath: string): Observable<AssetMappingConfig> {
  //   return this.http.get<AssetMappingConfig>(configPath).pipe(
  //     tap(config => console.log('📄 File JSON caricato:', Object.keys(config.fields || {}).length, 'campi')),
  //     catchError((error) => {
  //       console.error('❌ Errore nel caricamento del file JSON:', error);
  //       // Configurazione di fallback
  //       return of({
  //         version: "fallback",
  //         lastUpdated: new Date().toISOString(),
  //         description: "Configurazione di fallback",
  //         fields: {
  //           'uid': { type: 'string', label: 'UID', visible: true },
  //           'vin': { type: 'string', label: 'VIN', visible: false }, // VIN sempre presente
  //           'plate': { type: 'string', label: 'Identificativo', visible: true },
  //           'vehicle_status': { type: 'string', label: 'Stato Asset', visible: true }
  //         }
  //       } as AssetMappingConfig);
  //     })
  //   );
  // }

  /**
   * Sincronizza la configurazione JSON master con quella dell'API
   * Il JSON "comanda" - determina quali campi devono esistere
   */
  private syncConfigurations(
    jsonConfig: AssetMappingConfig,
    apiConfig: AssetMappingConfig | null,
    fullApiConfig?: any
  ): { config: AssetMappingConfig; hasChanges: boolean } {
    // console.log('🔄 Sincronizzazione configurazioni...');
    // console.log('📄 JSON campi:', Object.keys(jsonConfig.fields || {}).length);
    // console.log('🌐 API campi:', Object.keys(apiConfig?.fields || {}).length);

    const syncedFields: Record<string, AssetFieldConfig> = {};
    let hasChanges = false;

    // Il JSON "comanda" - prendi tutti i campi dal JSON
    Object.keys(jsonConfig.fields || {}).forEach(fieldKey => {
      const jsonField = jsonConfig.fields[fieldKey];
      // Match esatto sulla chiave scope|name, con fallback per nome sulle config salvate
      // prima della migrazione (chiave piatta = solo name).
      const apiField = apiConfig?.fields?.[fieldKey]
        ?? findFieldConfigByName(apiConfig, extractNameFromFieldConfigKey(fieldKey));

      if (apiField) {
        // Campo esiste in entrambi - mantieni la visibilità dall'API (preferenza utente)
        syncedFields[fieldKey] = {
          ...jsonField, // Struttura dal JSON
          visible: apiField.visible // Visibilità dall'API (preferenza utente)
        };
      } else {
        // Campo nuovo dal JSON - aggiungi con visible: true di default
        syncedFields[fieldKey] = {
          ...jsonField,
          visible: true
        };
        hasChanges = true;
        // console.log('➕ Nuovo campo aggiunto:', fieldKey);
      }
    });

    // Controlla se ci sono campi nell'API che non sono nel JSON (da rimuovere)
    if (apiConfig?.fields) {
      Object.keys(apiConfig.fields).forEach(fieldKey => {
        const name = extractNameFromFieldConfigKey(fieldKey);
        if (!findFieldConfigByName(jsonConfig, name)) {
          hasChanges = true;
          // console.log('➖ Campo rimosso:', fieldKey);
        }
      });
    }

    // Se non c'era configurazione API, è un cambiamento
    if (!apiConfig) {
      hasChanges = true;
      // console.log('🆕 Prima configurazione API');
    }

    // Estrai il customer dall'API config se disponibile
    const extractedCustomer = fullApiConfig?.config?.customers?.[0];

    const syncedConfig: AssetMappingConfig = {
      ...jsonConfig,
      fields: syncedFields,
      lastUpdated: new Date().toISOString(),
      version: hasChanges ? `${jsonConfig.version}-sync-${Date.now()}` : jsonConfig.version,
      audience: apiConfig?.audience || 'USER', // Mantieni l'audience esistente o fallback su 'USER'
      idCustomer: extractedCustomer || undefined // Salva il primo customer dall'API
    };

    console.log(`${hasChanges ? '🔄' : '✅'} Sincronizzazione completata:`,
                Object.keys(syncedFields).length, 'campi',
                hasChanges ? '(con modifiche)' : '(nessuna modifica)');

    if (extractedCustomer) {
      console.log('👤 Customer estratto dalla configurazione API:', extractedCustomer);
    }

    return { config: syncedConfig, hasChanges };
  }

  /**
   * Salva la configurazione mapping corrente
   */
  public saveMappingConfiguration(audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): void {
    if (!this.mappingConfig) {
      console.warn('Nessuna configurazione mapping da salvare');
      return;
    }

    this.catalogService.saveMappingConfiguration(this.mappingConfig, this.lastProcessedCustomerUid ?? '', audience)
      .pipe(takeUntil(this.customerChange$))
      .subscribe({
        next: () => this.mappingConfigUpdated$.next(this.mappingConfig!),
        error: (error) => console.error('Errore salvataggio mapping config:', error)
      });

    // IMPORTANTE: Aggiorna il store NgRx per propagare i cambiamenti ai componenti
    this.updateNgRxStore();
  }

  // ---- Homepage20 layout persistence ----

  public saveHomepageLayout(rowsOrder: string[], uidCustomer: string, uidUser: string, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): void {
    if (!uidCustomer || !uidUser) {
      console.warn('[saveHomepageLayout] Mancano uidCustomer o uidUser', { uidCustomer, uidUser });
      return;
    }

    const config: MappingConfig = {
      uidUser,
      scope: AdditionalSignalsScope.HOMEPAGE20_LAYOUT,
      audience,
      config: {
        name: 'Homepage20 Layout Configuration',
        description: 'Ordine sezioni homepage20',
        mappingData: { rowsOrder },
        state: 'active',
        type: 'layout',
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        customers: [uidCustomer]
      }
    };

    console.log('[saveHomepageLayout] Salvo layout:', { uidCustomer, uidUser, rowsOrder });
    this.createMappingConfig(config).subscribe({
      next: () => console.log('[saveHomepageLayout] Layout salvato:', rowsOrder),
      error: (e) => console.error('[saveHomepageLayout] Errore salvataggio:', e)
    });
  }

  public loadHomepageLayout(uidCustomer: string, uidUser: string): Observable<string[] | null> {
    const queryString = `?audience=USER&scope=${AdditionalSignalsScope.HOMEPAGE20_LAYOUT}&uidUser=${uidUser}&uidCustomer=${uidCustomer}`;
    return this.http.get(this.apiUrl + this.baseConfigEndpointGet + queryString).pipe(
      map((res: any) => {
        const data: MappingConfig[] = res.data || [];
        if (data.length > 0) {
          const rowsOrder = data[0].config?.mappingData?.rowsOrder;
          return Array.isArray(rowsOrder) ? rowsOrder : null;
        }
        return null;
      }),
      catchError(() => of(null))
    );
  }

  // ---- ConsumptionTrend20 layout persistence ----

  public saveTrend20Layout(rowsOrder: string[], uidCustomer: string, uidUser: string): void {
    if (!uidCustomer || !uidUser) {
      console.warn('[saveTrend20Layout] Mancano uidCustomer o uidUser', { uidCustomer, uidUser });
      return;
    }
    const config: MappingConfig = {
      uidUser,
      scope: AdditionalSignalsScope.TREND20_LAYOUT,
      audience: 'USER',
      config: {
        name: 'ConsumptionTrend20 Layout Configuration',
        description: 'Ordine sezioni consumption-trend20',
        mappingData: { rowsOrder },
        state: 'active',
        type: 'layout',
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        customers: [uidCustomer]
      }
    };
    this.createMappingConfig(config).subscribe({
      next: () => console.log('[saveTrend20Layout] Layout salvato:', rowsOrder),
      error: (e) => console.error('[saveTrend20Layout] Errore salvataggio:', e)
    });
  }

  public loadTrend20Layout(uidCustomer: string, uidUser: string): Observable<string[] | null> {
    const queryString = `?audience=USER&scope=${AdditionalSignalsScope.TREND20_LAYOUT}&uidUser=${uidUser}&uidCustomer=${uidCustomer}`;
    return this.http.get(this.apiUrl + this.baseConfigEndpointGet + queryString).pipe(
      map((res: any) => {
        const data: MappingConfig[] = res.data || [];
        if (data.length > 0) {
          const rowsOrder = data[0].config?.mappingData?.rowsOrder;
          return Array.isArray(rowsOrder) ? rowsOrder : null;
        }
        return null;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Salva la configurazione journey (KPI Journey)
   */
  public saveJourneyConfiguration(journeyData: any, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): Observable<any> {
    if (!journeyData) {
      console.warn('Nessuna configurazione journey da salvare');
      return of(null);
    }

    return this.saveJourneyConfigToAPI(journeyData, audience);
  }

  /**
   * Salva la configurazione journey tramite API
   */
  private saveJourneyConfigToAPI(journeyData: any, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): Observable<any> {
    return this.storeAnag.pipe(
      select(getApplySelectedCustomer),
      take(1),
      switchMap((selectedCustomer) => {
        const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
        const currentUidCustomer = selectedCustomer?.uid;
        const currentUidUser = userInfo.user?.uid;

        if (!currentUidCustomer || !currentUidUser) {
          console.warn('Impossibile salvare journey su API: mancano credenziali', {
            uidCustomer: !!currentUidCustomer,
            uidUser: !!currentUidUser
          });
          return of(null);
        }

        return this.saveJourneyConfigToAPIInternal(currentUidCustomer, currentUidUser, journeyData, audience);
      })
    );
  }

  /**
   * Metodo interno per salvare journey config su API
   */
  private saveJourneyConfigToAPIInternal(uidCustomer: string, uidUser: string, journeyData: any, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): Observable<any> {
    if (!uidCustomer || !uidUser || !journeyData) {
      console.warn('Impossibile salvare journey su API: mancano credenziali o dati');
      return throwError(() => new Error('Parametri mancanti per il salvataggio API journey'));
    }

    const journeyConfigData: MappingConfig = {
      uidUser: uidUser,
      scope: AdditionalSignalsScope.JOURNEY_CONFIG,  // 🔴 Scope diverso per journey
      audience: audience,
      config: {
        name: 'KPI Journey Configuration',
        description: 'Configurazione dei KPI visualizzati nel journey',
        mappingData: journeyData,  // I dati del journey
        state: 'active',
        type: 'journey',
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        customers: [uidCustomer]
      }
    };

    console.log('📤 SALVATAGGIO JOURNEY API - Dati da salvare:', JSON.stringify(journeyConfigData, null, 2));

    return this.createMappingConfig(journeyConfigData).pipe(
      tap(() => {
        console.log('✅ Configurazione journey salvata su API');
        // Invalida la cache per forzare il ricaricamento dei dati aggiornati
        const cacheKey = `journey_${uidCustomer}`;
        this.apiConfigCache.delete(cacheKey);
        console.log('🗑️ Cache journey invalidata per customer:', uidCustomer);
      }),
      catchError((error) => {
        console.error('Errore nel salvataggio configurazione journey su API:', error);
        return throwError(() => error);
      })
    );
  }


  /**
   * Carica la configurazione catalog salvata (Signals Catalog)
   */
  public loadCatalogConfiguration(audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): Observable<{ data: any, audience?: 'USER' | 'CUSTOMER' | 'GLOBAL' }> {
    return this.storeAnag.pipe(
      select(getApplySelectedCustomer),
      take(1),
      switchMap((selectedCustomer) => {
        const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
        const currentUidCustomer = selectedCustomer?.uid;
        const currentUidUser = userInfo.user?.uid;

        if (!currentUidCustomer || !currentUidUser) {
          console.warn('Impossibile caricare catalog: mancano credenziali', {
            uidCustomer: !!currentUidCustomer,
            uidUser: !!currentUidUser
          });
          return of({ data: null, audience: undefined });
        }

        // 🔧 Crea chiave unica per customer+audience
        const requestKey = `${currentUidCustomer}_${audience}`;

        // 🔧 Se c'è già una richiesta in corso per questa combinazione, riutilizzala
        const existingRequest = this.catalogConfigRequestsInProgress.get(requestKey);
        if (existingRequest) {
          console.log('⏳ loadCatalogConfiguration già in corso per customer+audience:', requestKey, '- riutilizzo richiesta esistente');
          return existingRequest;
        }

        console.log('📡 loadCatalogConfiguration - Nuova chiamata API per customer+audience:', requestKey);

        // Crea la nuova richiesta
        const request$ = this.getCatalogConfigFromAPI(currentUidCustomer, currentUidUser, audience).pipe(
          map((configs: MappingConfig[]) => {
            if (!configs || configs.length === 0) {
              console.log('📭 Nessuna configurazione catalog salvata trovata per audience:', audience);
              return { data: null, audience: undefined };
            }

            // Prendi la configurazione più recente
            const sortedConfigs = configs.sort((a, b) => {
              const dateA = new Date(a.config?.updatedAt || a.config?.createdAt || 0).getTime();
              const dateB = new Date(b.config?.updatedAt || b.config?.createdAt || 0).getTime();
              return dateB - dateA;
            });

            const latestConfig = sortedConfigs[0];
            console.log('✅ Configurazione catalog caricata con audience:', latestConfig?.audience || audience);
            return {
              data: latestConfig?.config?.mappingData || null,
              audience: latestConfig?.audience || audience
            };
          }),
          // 🔧 Rimuovi la richiesta dalla mappa quando completa
          tap(() => {
            this.catalogConfigRequestsInProgress.delete(requestKey);
          }),
          catchError((error) => {
            // 🔧 Rimuovi anche in caso di errore
            this.catalogConfigRequestsInProgress.delete(requestKey);
            throw error;
          }),
          // 🔧 Condividi il risultato tra tutti i subscriber
          shareReplay(1)
        );

        // 🔧 Salva la richiesta in corso
        this.catalogConfigRequestsInProgress.set(requestKey, request$);

        return request$;
      })
    );
  }

  /**
   * Salva la configurazione dashboard
   */
  public saveDashboardConfiguration(dashboardData: any, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): Observable<any> {
    if (!dashboardData) {
      console.warn('Nessuna configurazione dashboard da salvare');
      return of(null);
    }

    return this.saveDashboardConfigToAPI(dashboardData, audience);
  }

  /**
   * Salva la configurazione dashboard tramite API
   */
  private saveDashboardConfigToAPI(dashboardData: any, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): Observable<any> {
    return this.storeAnag.pipe(
      select(getApplySelectedCustomer),
      take(1),
      switchMap((selectedCustomer) => {
        const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
        const currentUidCustomer = selectedCustomer?.uid;
        const currentUidUser = userInfo.user?.uid;

        if (!currentUidCustomer || !currentUidUser) {
          console.warn('Impossibile salvare dashboard su API: mancano credenziali', {
            uidCustomer: !!currentUidCustomer,
            uidUser: !!currentUidUser
          });
          return of(null);
        }

        return this.saveDashboardConfigToAPIInternal(currentUidCustomer, currentUidUser, dashboardData, audience);
      })
    );
  }

  /**
   * Metodo interno per salvare dashboard config su API
   */
  private saveDashboardConfigToAPIInternal(uidCustomer: string, uidUser: string, dashboardData: any, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): Observable<any> {
    if (!uidCustomer || !uidUser || !dashboardData) {
      console.warn('Impossibile salvare dashboard su API: mancano credenziali o dati');
      return throwError(() => new Error('Parametri mancanti per il salvataggio API dashboard'));
    }

    const dashboardConfigData: MappingConfig = {
      uidUser: uidUser,
      scope: AdditionalSignalsScope.DASHBOARD_CONFIG,
      audience: audience,
      config: {
        name: 'Dashboard Configuration',
        description: 'Configurazione dei widget e layout della dashboard',
        mappingData: dashboardData,
        state: 'active',
        type: 'dashboard',
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        customers: [uidCustomer]
      }
    };

    console.log('📤 SALVATAGGIO DASHBOARD API - Dati da salvare:', JSON.stringify(dashboardConfigData, null, 2));

    return this.createMappingConfig(dashboardConfigData).pipe(
      tap(() => {
        console.log('✅ Configurazione dashboard salvata su API');
        // Invalida la cache per forzare il ricaricamento dei dati aggiornati
        const cacheKey = `dashboard_${uidCustomer}`;
        this.apiConfigCache.delete(cacheKey);
        console.log('🗑️ Cache dashboard invalidata per customer:', uidCustomer);
      }),
      catchError((error) => {
        console.error('Errore nel salvataggio configurazione dashboard su API:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * @param uidCustomer customer su cui scrivere la configurazione. Va passato dai pannelli in cui
   * l'utente sceglie esplicitamente il customer (general-setting): il customer applicato nello
   * store è il proprio, non quello selezionato, e senza questo la config finirebbe sul customer
   * sbagliato. Se omesso si ricade sullo store, per i chiamanti che non hanno una selezione.
   */
  saveGraphicsConfiguration(graphicsData: any, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER', uidCustomer?: string | null): Observable<any> {
    if (!graphicsData) {
      console.warn('Nessuna configurazione graphics da salvare');
      return of(null);
    }
    return this.saveGraphicsConfigToAPI(graphicsData, audience, uidCustomer);
  }

  private saveGraphicsConfigToAPI(graphicsData: any, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER', uidCustomer?: string | null): Observable<any> {
    return this.storeAnag.pipe(
      select(getApplySelectedCustomer),
      take(1),
      switchMap((selectedCustomer) => {
        const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
        // Il customer passato dal chiamante ha la precedenza sulla selezione nello store.
        const currentUidCustomer = uidCustomer || selectedCustomer?.uid;
        const currentUidUser = userInfo.user?.uid;
        if (!currentUidCustomer || !currentUidUser) {
          console.warn('Impossibile salvare graphics su API: mancano credenziali', {
            uidCustomer: !!currentUidCustomer,
            uidUser: !!currentUidUser
          });
          return of(null);
        }
        return this.saveGraphicsConfigToAPIInternal(currentUidCustomer, currentUidUser, graphicsData, audience);
      })
    );
  }

  private saveGraphicsConfigToAPIInternal(uidCustomer: string, uidUser: string, graphicsData: any, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): Observable<any> {
    if (!uidCustomer || !uidUser || !graphicsData) {
      console.warn('Impossibile salvare graphics su API: mancano credenziali o dati');
      return throwError(() => new Error('Parametri mancanti per il salvataggio API graphics'));
    }
    const graphicsConfigData: MappingConfig = {
      uidUser: uidUser,
      scope: AdditionalSignalsScope.GRAPHICS_CONFIG,
      audience: audience,
      config: {
        name: 'Graphics Configuration',
        description: 'Configurazione dei grafici e visualizzazioni personalizzate',
        mappingData: graphicsData,
        state: 'active',
        type: 'graphics',
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        customers: [uidCustomer]
      }
    };
    console.log('📤 SALVATAGGIO GRAPHICS API - Dati da salvare:', JSON.stringify(graphicsConfigData, null, 2))
    return this.createMappingConfig(graphicsConfigData).pipe(
      tap(() => {
        console.log('✅ Configurazione graphics salvata su API')
        const cacheKey = `graphics_${uidCustomer}`
        this.apiConfigCache.delete(cacheKey)
        console.log('🗑️ Cache graphics invalidata per customer:', uidCustomer
        )
      }),
      catchError((error) => {
        console.error('Errore nel salvataggio configurazione graphics su API:', error)
        return throwError(() => error)
      })
    );
    }

  /**
   * Aggiorna lo store NgRx con la configurazione corrente
   */
  private updateNgRxStore(): void {
    if (this.mappingConfig) {
    //  console.log('🔄 IOTASSETS SERVICE - Aggiornamento store NgRx con nuova configurazione...');
      this.store.dispatch(IotAssetActions.saveMappingConfig({
        mappingConfig: this.mappingConfig
      }));
     // console.log('✅ IOTASSETS SERVICE - Store NgRx aggiornato, i componenti dovrebbero reagire');
    }
  }

  /**
   * Forza l'aggiornamento della sidebar ripetendo il dispatch dell'azione NgRx
   */
  forceUpdateSidebar(): void {
  //  console.log('🚀 IOTASSETS SERVICE - Forzando aggiornamento sidebar...');
    if (this.mappingConfig) {
      // Re-dispatch dell'azione per forzare l'aggiornamento dei componenti
      this.store.dispatch(IotAssetActions.saveMappingConfig({
        mappingConfig: { ...this.mappingConfig } // Creiamo una nuova istanza per forzare il change detection
      }));
    //  console.log('✅ IOTASSETS SERVICE - Aggiornamento forzato completato');
    } else {
      console.warn('⚠️ IOTASSETS SERVICE - Impossibile forzare aggiornamento: configurazione non disponibile');
    }
  }

  // Nuovo metodo per salvare tramite API
  private saveMappingConfigToAPI(): void;
  private saveMappingConfigToAPI(audience: 'USER' | 'CUSTOMER' | 'GLOBAL'): void;
  private saveMappingConfigToAPI(uidCustomer: string, uidUser: string, config: AssetMappingConfig, audience?: 'USER' | 'CUSTOMER' | 'GLOBAL'): Observable<any>;
  private saveMappingConfigToAPI(uidCustomerOrAudience?: string | 'USER' | 'CUSTOMER' | 'GLOBAL', uidUser?: string, config?: AssetMappingConfig, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): void | Observable<any> {
    // Caso 1: Solo audience passato come primo parametro
    if (typeof uidCustomerOrAudience === 'string' && (uidCustomerOrAudience === 'USER' || uidCustomerOrAudience === 'CUSTOMER' || uidCustomerOrAudience === 'GLOBAL') && !uidUser && !config) {
      audience = uidCustomerOrAudience;
    }
    // Caso 2: Tutti i parametri passati (uidCustomer, uidUser, config, audience)
    else if (typeof uidCustomerOrAudience === 'string' && uidUser && config && uidCustomerOrAudience !== 'USER' && uidCustomerOrAudience !== 'CUSTOMER' && uidCustomerOrAudience !== 'GLOBAL') {
      return this.saveMappingConfigToAPIInternal(uidCustomerOrAudience, uidUser, config, audience);
    }

    // Altrimenti usa il customer dallo store NgRx
    this.storeAnag.pipe(
      select(getApplySelectedCustomer),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe((selectedCustomer) => {
      const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
      const currentUidCustomer = selectedCustomer?.uid;
      const currentUidUser = userInfo.user?.uid;

      if (!currentUidCustomer || !currentUidUser || !this.mappingConfig) {
        console.warn('Impossibile salvare su API: mancano credenziali o configurazione', {
          uidCustomer: !!currentUidCustomer,
          uidUser: !!currentUidUser,
          hasConfig: !!this.mappingConfig
        });
        return;
      }

      this.saveMappingConfigToAPIInternal(currentUidCustomer, currentUidUser, this.mappingConfig, audience).pipe(
        takeUntil(this.customerChange$) // 🔧 Cancella se cambia customer
      ).subscribe({
        next: () => console.log('Configurazione mapping salvata tramite API per customer:', currentUidCustomer),
        error: (error) => console.error('Errore nel salvataggio della configurazione mapping tramite API:', error)
      });
    });
  }

  // private saveMappingConfigToAPIInternal(uidCustomer: string, uidUser: string, config: AssetMappingConfig, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): Observable<any> {
  //   if (!uidCustomer || !uidUser || !config) {
  //     console.warn('Impossibile salvare su API: mancano credenziali utente o configurazione');
  //     return throwError(() => new Error('Parametri mancanti per il salvataggio API'));
  //   }

  //   // Prima controlla se esiste già una configurazione
  //   return this.getMappingConfigFromAPI(uidCustomer,uidUser).pipe(
  //     switchMap((existingConfigs) => {
  //       /// #FZ09102025 per MIRKO elimnianre customer
  //       uidCustomer = uidCustomer;
  //       const mappingConfigData: MappingConfig = {
  //         uidUser: uidUser,
  //         scope: AdditionalSignalsScope.MAPPING_CONFIG,
  //         audience: audience,
  //         config: {
  //           name: 'Vehicle Mapping Configuration',
  //           description: 'Configurazione di mapping per i campi dei veicoli IoT',
  //           mappingData: config,
  //           state: 'active',
  //           type: 'mapping',
  //           updatedAt: new Date().toISOString(),
  //           customers: [uidCustomer]
  //         }
  //       };

  //       // 🔍 LOG DETTAGLIATO: Cosa stiamo per salvare
  //       console.log('📤 SALVATAGGIO API - Dati da salvare:');
  //       console.log('  - Customer:', uidCustomer);
  //       console.log('  - User:', uidUser);
  //       console.log('  - Configurazioni esistenti trovate:', existingConfigs?.length || 0);
  //       // console.log('  - Numero campi nella config:', Object.keys(config.fields || {}).length);
  //       console.log('  - Versione config:', config.version);
  //       // console.log('  - Primi 10 campi:', Object.keys(config.fields || {}).slice(0, 10));
  //       console.log('  - Configurazione completa da salvare:', JSON.stringify(mappingConfigData, null, 2));

  //       if (existingConfigs && existingConfigs.length > 0) {
  //         const existingConfig = existingConfigs[0];
  //         const existingAudience = existingConfig.audience;
  //         const existingCustomers = existingConfig.config?.customers || [];
  //         const newAudience = audience;
  //         const newCustomer = uidCustomer;

  //         // Verifica se il customer corrente è già nella lista customers della configurazione esistente
  //         // SOLO per audience = 'CUSTOMER'
  //         const customerChanged = (newAudience === 'CUSTOMER') ? !existingCustomers.includes(newCustomer) : false;

  //         console.log('🔍 CONTROLLO AUDIENCE E CUSTOMER:', {
  //           existingAudience: existingAudience,
  //           newAudience: newAudience,
  //           audienceChanged: existingAudience !== newAudience,
  //           existingCustomers: existingCustomers,
  //           newCustomer: newCustomer,
  //           customerChanged: customerChanged,
  //           checkCustomer: newAudience === 'CUSTOMER'
  //         });

  //         // Determina se fare PUT o POST
  //         let shouldUpdate = false;

  //         if (existingAudience === newAudience) {
  //           if (newAudience === 'CUSTOMER') {
  //             // Per audience CUSTOMER: controlla anche il customer
  //             shouldUpdate = !customerChanged;
  //           } else {
  //             // Per audience USER/GLOBAL: ignora il customer
  //             shouldUpdate = true;
  //           }
  //         }

  //         if (shouldUpdate) {
  //           // PUT: Aggiornamento configurazione esistente
  //           mappingConfigData.id = existingConfig.id;
  //           console.log('🔄 PUT: Aggiornamento configurazione esistente con ID:', existingConfig.id);
  //           console.log(`   Motivo: Audience invariata (${newAudience})${newAudience === 'CUSTOMER' ? ' e customer invariato' : ''}`);
  //           return this.editMappingConfig(mappingConfigData);
  //         } else {
  //           // POST: Nuova configurazione
  //           mappingConfigData.config.createdAt = new Date().toISOString();
  //           console.log('🆕 POST: Creazione nuova configurazione');

  //           if (existingAudience !== newAudience) {
  //             console.log(`   Motivo: Audience cambiata da ${existingAudience} a ${newAudience}`);
  //           }
  //           if (newAudience === 'CUSTOMER' && customerChanged) {
  //             console.log(`   Motivo: Customer aggiunto per audience CUSTOMER: ${newCustomer}`);
  //           }

  //           return this.createMappingConfig(mappingConfigData);
  //         }
  //       } else {
  //         // Nessuna configurazione esistente: crea nuova configurazione
  //         mappingConfigData.config.createdAt = new Date().toISOString();
  //         console.log('🆕 Nessuna configurazione esistente - POST: Creazione nuova configurazione');
  //         return this.createMappingConfig(mappingConfigData);
  //       }
  //     }),
  //     tap(() => {
  //       console.log('✅ Configurazione mapping salvata su API');
  //       // Emetti la notifica che la configurazione è stata aggiornata
  //       this.mappingConfigUpdated$.next(config);
  //     }),
  //     catchError((error) => {
  //       console.error('Errore nel salvataggio configurazione su API:', error);
  //       return throwError(() => error);
  //     })
  //   );
  // }

  // ...existing code...
private saveMappingConfigToAPIInternal(uidCustomer: string, uidUser: string, config: AssetMappingConfig, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): Observable<any> {
  if (!uidCustomer || !uidUser || !config) {
    console.warn('Impossibile salvare su API: mancano credenziali utente o configurazione');
    return throwError(() => new Error('Parametri mancanti per il salvataggio API'));
  }

  // ✅ Aggiorna l'audience nella configurazione prima di salvare
  config.audience = audience;

  const mappingConfigData: MappingConfig = {
    uidUser: uidUser,
    scope: AdditionalSignalsScope.MAPPING_CONFIG,
    audience: audience,
    config: {
      name: 'Vehicle Mapping Configuration',
      description: 'Configurazione di mapping per i campi dei veicoli IoT',
      mappingData: config,
      state: 'active',
      type: 'mapping',
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
      customers: [uidCustomer]
    }
  };

  // 🔍 LOG SEMPLIFICATO: Cosa stiamo per salvare
  console.log('📤 SALVATAGGIO API - Dati da salvare (POST semplificata):');
  console.log('  - Customer:', uidCustomer);
  console.log('  - User:', uidUser);
  console.log('  - Audience:', audience);
  console.log('  - Versione config:', config.version);
  console.log('  - Configurazione completa da salvare:', JSON.stringify(mappingConfigData, null, 2));

  // 🆕 SEMPRE POST: Il BE gestisce internamente duplicati/aggiornamenti
  console.log('🆕 POST: Salvataggio configurazione (gestione duplicati lato BE)');

  return this.createMappingConfig(mappingConfigData).pipe(
    tap(() => {
      console.log('✅ Configurazione mapping salvata su API');
      // Emetti la notifica che la configurazione è stata aggiornata
      this.mappingConfigUpdated$.next(config);
    }),
    catchError((error) => {
      console.error('Errore nel salvataggio configurazione su API:', error);
      return throwError(() => error);
    })
  );
}
// ...existing code...

  // // Metodo per aggiornare la configurazione e salvarla
  // public updateFieldConfiguration(fields: Record<string, AssetFieldConfig>): void {
  //   if (this.mappingConfig) {
  //     this.mappingConfig.fields = fields;
  //     this.mappingConfig.lastUpdated = new Date().toISOString();
  //     this.saveMappingConfigToStorage();
  //     console.log('Configurazione campi aggiornata e salvata');
  //   }
  // }

  // Metodo per ottenere le chiavi mappate
  public getMappedKeys(): string[] {
    return this.mappingConfig ? Object.keys(this.mappingConfig.fields) : [];
  }

  // Metodo per ottenere solo le chiavi visibili
  public getVisibleKeys(): string[] {
    if (!this.mappingConfig) return [];
    return Object.keys(this.mappingConfig.fields).filter(key =>
      this.mappingConfig!.fields[key].visible !== false // visible by default se non specificato
    );
  }

  // Metodo per ottenere tutte le chiavi mappate (include sempre VIN anche se invisible)
  public getAllMappedKeys(): string[] {
    if (!this.mappingConfig) return ['vin']; // Almeno il VIN come fallback
    const keys = Object.keys(this.mappingConfig.fields);
    // Assicurati che VIN sia sempre incluso
    if (!keys.includes('vin')) {
      keys.push('vin');
    }
    return keys;
  }

  // Metodo per controllare se un campo è visibile
  public isFieldVisible(fieldName: string): boolean {
    const fieldConfig = findFieldConfigByName(this.mappingConfig, fieldName);
    if (!fieldConfig) return false;
    return fieldConfig.visible !== false; // visible by default
  }

  // Metodo per ottenere il tipo di un campo
  public getFieldType(fieldName: string): string {
    return findFieldConfigByName(this.mappingConfig, fieldName)?.type || 'any';
  }

  // Metodo per ottenere il label di un campo
  public getFieldLabel(fieldName: string): string {
    return findFieldConfigByName(this.mappingConfig, fieldName)?.label || fieldName;
  }

  // Metodo per ottenere tutti i campi con i loro metadati
  public getFieldsMetadata(): Record<string, AssetFieldConfig> {
    return this.mappingConfig?.fields || {};
  }

  // Metodo per ottenere la configurazione corrente
  public getMappingConfig(): AssetMappingConfig | null {
    return this.mappingConfig;
  }

  // Metodo per impostare la configurazione (usato dal settings-panel)
  public setMappingConfig(config: AssetMappingConfig): void {
    this.mappingConfig = config;
    this.updateNgRxStore();
  }

  /**
   * Verifica se un campo deve essere visibile per un veicolo specifico
   * basandosi sul can_map_name del veicolo e sull'array availableInModels del campo
   */
  public isFieldVisibleForVehicle(fieldKey: string, vehicleCanMapName: string): boolean {
    // Controlli di base
    if (!this.mappingConfig || !this.mappingConfig.fields || !vehicleCanMapName) {
      return false;
    }

    const fieldConfig = findFieldConfigByName(this.mappingConfig, fieldKey);

    // Se il campo non esiste nella configurazione, non mostrarlo
    if (!fieldConfig) {
      return false;
    }

    // Se il campo è configurato come non visibile nelle settings, non mostrarlo
    if (fieldConfig.visible === false) {
      return false;
    }

    // Se il campo NON ha availableInModels, è un campo COMUNE - mostralo sempre (se visibile nelle settings)
    if (!fieldConfig.availableInModels || !Array.isArray(fieldConfig.availableInModels) || fieldConfig.availableInModels.length === 0) {
      return true;
    }

    // Se il campo HA availableInModels, è un campo SPECIFICO - mostralo solo se il can_map_name del veicolo è nell'array
    const isCompatible = fieldConfig.availableInModels.includes(vehicleCanMapName);

    return isCompatible;
  }

  /**
   * Ottiene la configurazione dei campi filtrata per un veicolo specifico
   * Solo i campi compatibili con il can_map_name del veicolo saranno inclusi
   */
  public getVisibleFieldsForVehicle(vehicleCanMapName: string): AssetMappingConfig | null {
    if (!this.mappingConfig || !vehicleCanMapName) {
      return this.mappingConfig;
    }

    // Crea una copia della configurazione per non modificare l'originale
    const filteredConfig: AssetMappingConfig = {
      ...this.mappingConfig,
      fields: {}
    };

    // Filtra i campi basandosi sulla compatibilità con il modello del veicolo
    Object.keys(this.mappingConfig.fields).forEach(fieldKey => {
      if (this.isFieldVisibleForVehicle(fieldKey, vehicleCanMapName)) {
        filteredConfig.fields[fieldKey] = this.mappingConfig!.fields[fieldKey];
      }
    });

    return filteredConfig;
  }

  // Metodo per ricaricare la configurazione a runtime
  public reloadMappingConfiguration(): Observable<boolean> {
    console.log('🔄 Ricaricamento configurazione mapping...');

    // Reset dei flag e ricarica completa con sincronizzazione
    this.configLoaded = false;
    this.mappingConfig = null;
    this.lastProcessedCustomerUid = null; // 🔧 Reset ultimo customer per forzare reload

    // 🔧 Pulisci la cache API per forzare il reload
    this.clearApiConfigCache();

    return new Observable<boolean>((observer) => {
      // Utilizza il metodo completo di sincronizzazione dal catalogo
      this.loadMappingFromAPI();

      // Aspetta un po' per il completamento asincrono
      setTimeout(() => {
        if (this.configLoaded && this.mappingConfig) {
          console.log('✅ Configurazione ricaricata con successo');
          observer.next(true);
          observer.complete();
        } else {
          console.log('❌ Errore nel ricaricamento configurazione');
          observer.next(false);
          observer.complete();
        }
      }, 2000);
    });
  }

  /**
   * Metodo pubblico per testare la sincronizzazione dalla console del browser
   * Utilizzabile con: window.iotAssetsService.forceSyncConfiguration()
   */
  public forceSyncConfiguration(): void {
    console.log('🔧 FORZATURA SINCRONIZZAZIONE CONFIGURAZIONE');
    this.reloadMappingConfiguration().subscribe({
      next: (success) => {
        if (success) {
          console.log('✅ Sincronizzazione completata con successo');
          // console.log('📋 Campi attuali:', Object.keys(this.mappingConfig?.fields || {}));
          console.log('💡 Per aggiornare le mappe, ricarica la pagina o vai su una nuova mappa');
        } else {
          console.log('❌ Errore nella sincronizzazione');
        }
      },
      error: (error) => console.error('❌ Errore:', error)
    });
  }

  /**
   * Metodo pubblico per pulire la cache API dalla console del browser
   * Utilizzabile con: window.iotAssetsService.clearApiCache()
   */
  public clearApiCache(): void {
    this.clearApiConfigCache();
    console.log('🧹 Cache API pulita manualmente');
  }

  /**
   * Metodo pubblico per verificare lo stato della configurazione
   * Utilizzabile con: window.iotAssetsService.checkConfigStatus()
   */
  public checkConfigStatus(): void {
    console.log('📊 STATO CONFIGURAZIONE IOTASSETS SERVICE:');
    console.log('  - configLoaded:', this.configLoaded);
    console.log('  - configLoading:', this.configLoading);
    console.log('  - mappingConfig presente:', !!this.mappingConfig);
    // console.log('  - Campi disponibili:', Object.keys(this.mappingConfig?.fields || {}).length);
    console.log('  - Versione config:', this.mappingConfig?.version);
    console.log('  - Cache API entries:', this.apiConfigCache.size);

    if (this.mappingConfig) {
      // console.log('  - Campi visibili:', this.getVisibleKeys().length);
      // console.log('  - Primi 5 campi:', Object.keys(this.mappingConfig.fields).slice(0, 5));
    }
  }

  /**
   * Metodo pubblico per forzare la cancellazione delle operazioni in corso
   * Utilizzabile con: window.iotAssetsService.cancelPendingOperations()
   */
  public cancelPendingOperations(): void {
    this.customerChange$.next();
    console.log('🛑 Operazioni in corso cancellate manualmente');
  }

  // Metodi pubblici per NgRx Store
  public loadMappingConfigForStore(audience: "USER" | "CUSTOMER" | "GLOBAL" = "USER"): Observable<AssetMappingConfig> {
    //console.log('📖 IOTASSETS SERVICE - Lettura configurazione mapping dallo store NgRx...');

    return this.store.pipe(
      select(selectMappingConfig),
      tap(config => {
        if (config) {
          // console.log('✅ IOTASSETS SERVICE - Configurazione trovata nello store NgRx:', {
          //   version: config.version,
          //   fieldsCount: Object.keys(config.fields || {}).length,
          //   audience: config.audience
          // });

          // Aggiorna anche la variabile locale per coerenza
          // Crea una nuova istanza per evitare errori su oggetti frozen/immutable dal store
          this.mappingConfig = { ...config, audience: audience };
        } else {
          console.log('⚠️ IOTASSETS SERVICE - Nessuna configurazione trovata nello store NgRx');
        }
      }),
      filter((config): config is AssetMappingConfig => config !== null && config !== undefined), // Type guard per filtrare valori validi
      takeUntil(this.destroy$)
    );
  }

  // public saveMappingConfigForStore(config: AssetMappingConfig): Observable<AssetMappingConfig> {
  //   return new Observable<AssetMappingConfig>((observer) => {
  //     this.mappingConfig = config;

  //     // Salva nel localStorage
  //     try {
  //       localStorage.setItem('vehicle-mapping-config', JSON.stringify(config));
  //     } catch (error) {
  //       console.warn('Errore nel salvataggio localStorage:', error);
  //     }

  //     // Salva tramite API usando customer dallo store NgRx
  //     this.storeAnag.pipe(
  //       select(getApplySelectedCustomer),
  //       takeUntil(this.destroy$)
  //     ).subscribe((selectedCustomer) => {
  //       const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
  //       const uidCustomer = selectedCustomer?.uid;
  //       const uidUser = userInfo.user?.uid;

  //     if (uidCustomer && uidUser) {
  //       // Controlla se esiste già una configurazione
  //       this.getMappingConfigFromAPI(uidCustomer, uidUser).pipe(
  //         takeUntil(this.customerChange$) // 🔧 Cancella se cambia customer
  //       ).subscribe({
  //         next: (existingConfigs) => {
  //           const mappingConfigData: MappingConfig = {
  //             uidCustomer: uidCustomer,
  //             uidUser: uidUser,
  //             scope: AdditionalSignalsScope.MAPPING_CONFIG,
  //             config: {
  //               name: 'Vehicle Mapping Configuration',
  //               description: 'Vehicle mapping configuration',
  //               mappingData: config,
  //               state: 'active',
  //               type: 'mapping',
  //               updatedAt: new Date().toISOString(),
  //               customers: [uidCustomer]
  //             }
  //           };

  //           if (existingConfigs && existingConfigs.length > 0) {
  //             // Aggiorna esistente
  //             const existingConfig = existingConfigs[0];
  //             mappingConfigData.id = existingConfig.id;
  //             this.editMappingConfig(mappingConfigData).pipe(
  //               takeUntil(this.customerChange$) // 🔧 Cancella se cambia customer
  //             ).subscribe({
  //               next: () => {
  //                 observer.next(config);
  //                 observer.complete();
  //               },
  //               error: (error) => {
  //                 console.warn('Errore aggiornamento API, continuo con localStorage:', error);
  //                 observer.next(config);
  //                 observer.complete();
  //               }
  //             });
  //           } else {
  //             // Crea nuovo
  //             this.createMappingConfig(mappingConfigData).pipe(
  //               takeUntil(this.customerChange$) // 🔧 Cancella se cambia customer
  //             ).subscribe({
  //               next: () => {
  //                 observer.next(config);
  //                 observer.complete();
  //               },
  //               error: (error) => {
  //                 console.warn('Errore creazione API, continuo con localStorage:', error);
  //                 observer.next(config);
  //                 observer.complete();
  //               }
  //             });
  //           }
  //         },
  //         error: (error) => {
  //           console.warn('Errore accesso API, continuo con localStorage:', error);
  //           observer.next(config);
  //           observer.complete();
  //         }
  //       });
  //       } else {
  //         // Solo localStorage se non abbiamo credenziali
  //         observer.next(config);
  //         observer.complete();
  //       }
  //     });
  //   });
  // }

  /**
   * Estrae i campi essenziali sempre disponibili dalla struttura base IotAssetRawData
   * come fallback quando non sono presenti nel catalogo
   */
  private extractEssentialFieldsFromRawData(item: IotAssetRawData): { [key: string]: any } {
    const essentialFields: { [key: string]: any } = {};

    // Campi sempre disponibili nella struttura base IotAssetRawData
    if (item.vin) {
      essentialFields.vin = item.vin;
    }

    if (item.can_map_name) {
      essentialFields.can_map_name = item.can_map_name;
    }

    // Possiamo aggiungere altri campi se dovessero essere sempre disponibili nella struttura base
    // Esempio: se in futuro IotAssetRawData avrà altri campi essenziali

    if (!environment.production && Object.keys(essentialFields).length > 0) {
      console.log(`📋 Campi essenziali estratti dalla struttura base per VIN ${item.vin}:`, Object.keys(essentialFields));
    }

    return essentialFields;
  }

  /**
   * Metodo di debugging per testare il sistema di fallback VIN
   */
  public debugVinFallback(testData: IotAssetRawData): void {
    console.log('🧪 TEST FALLBACK VIN - Simulazione scenario senza VIN nel catalogo');
    console.log('📋 Dati di test:', {
      vin: testData.vin,
      can_map_name: testData.can_map_name,
      scopesCount: testData.scopes?.length || 0
    });

    const essentialFields = this.extractEssentialFieldsFromRawData(testData);
    console.log('✅ Campi essenziali estratti:', essentialFields);

    if (essentialFields.vin) {
      console.log('✅ VIN fallback funziona correttamente:', essentialFields.vin);
    } else {
      console.error('❌ VIN fallback non funziona - VIN non trovato nei dati base');
    }
  }

  private processAssetData(item: any, mappings?: any): any {
    if (!this.mappingConfig) {
      console.warn('❌ ERRORE CRITICO: Configurazione mapping non caricata, i dati NON verranno processati correttamente!');
      console.warn('🔍 DEBUG: configLoaded =', this.configLoaded, ', mappingConfig =', !!this.mappingConfig);
      console.warn('🚚 Struttura item raw:', {
        vin: item.vin,
        hasScopes: !!item.scopes,
        scopesCount: item.scopes?.length || 0,
        directLatLon: { lat: item.lat_current, lon: item.lon_current }
      });
      return item; // Restituisce i dati raw senza processing
    }

    // 🔍 DEBUG: Mostra la struttura completa dei dati in arrivo dall'API
    // console.log('🚚 DEBUG PROCESS ASSET DATA - Item ricevuto:', {
    //   vin: item.vin,
    //   scopesCount: item.scopes?.length || 0,
    //   scopeNames: item.scopes?.map((s: any) => s.scope).join(', '),
    //   firstScopeAttributes: item.scopes?.[0]?.attributes?.length || 0
    // });

    if (!environment.production) {
      // console.log('🔍 Processing asset data per VIN:', item.vin);
      // console.log('📋 Scopes disponibili:', item.scopes?.map((s: any) => s.scope).join(', '));
    }

    // Crea un oggetto con i valori estratti dai scopes
    const mappedData: any = {};
    const unmappedFields: any[] = [];

    // Ottieni la configurazione dei campi dal catalogo
    const fieldsConfig = this.mappingConfig.fields;

    // Per ogni campo nella configurazione del catalogo
    Object.keys(fieldsConfig).forEach(storageKey => {
      const fieldConfig = fieldsConfig[storageKey];
      const fieldKey = extractNameFromFieldConfigKey(storageKey);
      const expectedScope = fieldConfig.scope;

      // Trova il scope corrispondente nella risposta
      const scopeData = item.scopes?.find((s: any) => s.scope === expectedScope);

      if (scopeData) {
        // Cerca l'attributo nel scope
        const attribute = scopeData.attributes?.find((attr: any) => attr.name === fieldKey);

        if (attribute) {
          // Converti il valore nel tipo appropriato
          let value = attribute.value;
          if (value !== null && fieldConfig.type === 'number' && typeof value === 'string') {
            const numValue = parseFloat(value);
            value = isNaN(numValue) ? value : numValue;
          }

          mappedData[fieldKey] = value;
          if (!environment.production) {
            // console.log(`✅ Campo trovato: ${fieldKey} = ${value} (scope: ${expectedScope})`);
          }
        } else {
          if (!environment.production) {
            // console.log(`⚠️ Campo ${fieldKey} non trovato nel scope ${expectedScope}`);
          }
          mappedData[fieldKey] = null; // Campo previsto ma non presente
        }
      } else {
        if (!environment.production) {
          // console.log(`❌ Scope ${expectedScope} non trovato per il campo ${fieldKey}`);
        }
        mappedData[fieldKey] = null; // Scope non presente
      }
    });

    // Raccogli tutti i campi non mappati da tutti gli scopes
    const mappedFieldNames = new Set(Object.keys(fieldsConfig).map(k => extractNameFromFieldConfigKey(k)));
    item.scopes?.forEach((scopeData: any) => {
      scopeData.attributes?.forEach((attr: any) => {
        if (!mappedFieldNames.has(attr.name)) {
          unmappedFields.push({
            key: attr.name,
            value: attr.value,
            scope: scopeData.scope
          });
        }
      });
    });

    // 🔧 FALLBACK PER CAMPI ESSENZIALI: Estrai i campi essenziali sempre disponibili
    // dalla struttura base IotAssetRawData se non sono presenti nel mapping
    const essentialFields = this.extractEssentialFieldsFromRawData(item);
    const fallbackFields: string[] = [];

    Object.keys(essentialFields).forEach(fieldKey => {
      if (mappedData[fieldKey] === null || mappedData[fieldKey] === undefined) {
        mappedData[fieldKey] = essentialFields[fieldKey];
        fallbackFields.push(fieldKey);
      }
    });

    // Log dei campi utilizzati come fallback
    if (fallbackFields.length > 0) {
      console.log(`🔧 Campi essenziali utilizzati come fallback da IotAssetRawData per VIN ${item.vin}: ${fallbackFields.join(', ')}`);
    }

    // Estrai can_map_name direttamente dai dati raw (indipendentemente dalla configurazione catalogo)
    let can_map_name: string | null = null;
    if (item.can_map_name) {
      // Se è già presente direttamente nei dati raw
      can_map_name = item.can_map_name;
    } else if (item.scopes) {
      // Cerca can_map_name in tutti gli scopes
      for (const scopeData of item.scopes) {
        if (scopeData.attributes) {
          const canMapAttribute = scopeData.attributes.find((attr: any) => attr.name === 'can_map_name');
          if (canMapAttribute && canMapAttribute.value) {
            can_map_name = canMapAttribute.value;
            break;
          }
        }
      }
    }

    // Aggiungi l'UID dal mapping vehicles se disponibile
    let uid: string | null = null;
    if (mappings?.vehicles && mappedData.vin) {
      // Trova l'UID corrispondente al VIN nel mapping
      for (const [key, value] of Object.entries(mappings.vehicles)) {
        if (value === mappedData.vin) {
          uid = key;
          break;
        }
      }
    }

    // Calcola i valori derivati usando i campi mappati
    const status_entered_at = mappedData.status_entered_at;
    const updated_at = mappedData.updated_at;
    const vehicle_status = mappedData.vehicle_status;

    // Prepara l'oggetto base con i campi calcolati e di utilità
    const processedData = {
      ...mappedData,
      uid, // Aggiungi l'UID estratto dal mapping
      can_map_name, // Aggiungi il can_map_name estratto dai dati raw
      additional_signals: null, // Non più disponibile nella nuova struttura

      // Calcola lo stato utilizzando i valori mappati
      vehicle_status: vehicle_status ? this.checkAssetConnection(status_entered_at, vehicle_status) : 'UNKNOWN',
      fromtime_last_update_status: status_entered_at ? this.getSecondsDiffFromNow(status_entered_at) : null,
      fromtime_last_update_event: updated_at ? this.getSecondsDiffFromNow(updated_at) : null,

      jsonRow: item, // Mantieni la riga originale completa
      unmappedFields, // Mantieni i campi non mappati per riferimento con scope
    };

    if (!environment.production) {
      // console.log(`✅ Asset processato: ${Object.keys(mappedData).length} campi mappati, ${unmappedFields.length} non mappati, ${fallbackFields.length} fallback`);
      // console.log(`🔧 can_map_name estratto: ${can_map_name} per VIN: ${mappedData.vin}`);
    }

    return processedData;
  }

  // Nuovo metodo asincrono per processare l'asset con indirizzo
  public processAssetDataWithAddress(item: any, mappings?: any): Observable<any> {
    const baseData = this.processAssetData(item, mappings);

    // Usa le coordinate dai dati processati (baseData) invece che dai dati raw (item)
    const lat = baseData.lat_current;
    const lon = baseData.lon_current;

    // Se abbiamo le coordinate, aggiungi l'indirizzo asincrono
    if (lat != null && lon != null) {
      return this.getAddressFromCoordinates(lat, lon).pipe(
        map(address => ({
          ...baseData,
          addressCurrent: address
        })),
        catchError(error => {
          console.warn('Errore nel recupero indirizzo per coordinate:', lat, lon, error);
          return of({
            ...baseData,
            addressCurrent: `${lat.toFixed(6)}, ${lon.toFixed(6)}`
          });
        }),
        timeout(8000) // 8 secondi massimo per evitare attese troppo lunghe
      ).pipe(
        catchError(timeoutError => {
          console.warn('Timeout nel recupero indirizzo, usando fallback coordinate');
          return of({
            ...baseData,
            addressCurrent: `${lat.toFixed(6)}, ${lon.toFixed(6)}`
          });
        })
      );
    } else {
      // Se non abbiamo coordinate, restituisci i dati base senza indirizzo
      if (!environment.production) {
        console.log(`⚠️ Coordinate mancanti per VIN ${baseData.vin}: lat=${lat}, lon=${lon}`);
      }
      return of(baseData);
    }
  }

  public processVehiclesWithAddress(vehicles: any[], mappings?: any): Observable<any[]> {
  if (!vehicles || vehicles.length === 0) {
    return of([]);
  }
  // Processa solo i dati base, senza indirizzi
  const processedVehicles = vehicles.map(vehicle => this.processAssetData(vehicle, mappings));
  return of(processedVehicles);
}
//   // Metodo pubblico per processare un array di veicoli con indirizzi
//   public processVehiclesWithAddress(vehicles: any[], mappings?: any): Observable<any[]> {
//     if (!vehicles || vehicles.length === 0) {
//       return of([]);
//     }

//     // 🔍 DEBUG: Verifica i dati raw che arrivano per il processVehiclesWithAddress
// //    console.log('🏠 DEBUG PROCESS VEHICLES WITH ADDRESS - Veicoli ricevuti:', vehicles.length);
//     vehicles.slice(0, 2).forEach((vehicle, index) => {
//       // console.log(`🏠 DEBUG PROCESS VEHICLES WITH ADDRESS - Veicolo ${index + 1}:`, {
//       //   vin: vehicle.vin,
//       //   scopesCount: vehicle.scopes?.length || 0,
//       //   hasLatCurrent: !!vehicle.lat_current,
//       //   hasLonCurrent: !!vehicle.lon_current,
//       //   latCurrentValue: vehicle.lat_current,
//       //   lonCurrentValue: vehicle.lon_current
//       // });
//     });

//     // 🛡️ SAFETY CHECK: Se i veicoli sono già processati (no scopes ma con coordinate),
//     // aggiungi solo gli indirizzi senza riprocessare i dati base
//     const hasValidCoordinates = vehicles.every(v =>
//       v.lat_current !== null && v.lat_current !== undefined &&
//       v.lon_current !== null && v.lon_current !== undefined
//     );
//     const hasNoScopes = vehicles.every(v => !v.scopes || v.scopes.length === 0);

//     if (hasNoScopes && hasValidCoordinates) {
//       console.log('🛡️ SAFETY: Veicoli già processati, aggiungo solo indirizzi');
//       // I veicoli sono già processati, aggiungi solo gli indirizzi
//       const processedVehicles$ = vehicles.map(vehicle =>
//         this.addAddressToVehicle(vehicle)
//       );
//       return forkJoin(processedVehicles$).pipe(
//         catchError(error => {
//           console.warn('Errore nel processare indirizzi per veicoli già processati:', error);
//           return of(vehicles); // Ritorna i veicoli originali se fallisce
//         })
//       );
//     }

//     // Processa ogni veicolo singolarmente e combina i risultati (processing completo)
//     console.log('🔄 PROCESSING: Processamento completo dei veicoli');
//     const processedVehicles$ = vehicles.map(vehicle =>
//       this.processAssetDataWithAddress(vehicle, mappings)
//     );

//     // Usa forkJoin per attendere che tutti gli indirizzi siano risolti
//     return forkJoin(processedVehicles$).pipe(
//       catchError(error => {
//         console.warn('Errore nel processare alcuni veicoli con indirizzi:', error);
//         // In caso di errore, restituisci i veicoli processati senza indirizzi
//         return of(vehicles.map(vehicle => this.processAssetData(vehicle, mappings)));
//       })
//     );
//   }

  // Metodo per aggiungere solo l'indirizzo a un veicolo già processato
  private addAddressToVehicle(vehicle: any): Observable<any> {
    // Se il veicolo non ha coordinate valide, ritorna il veicolo senza modifiche
    if (!vehicle.lat_current || !vehicle.lon_current) {
      return of(vehicle);
    }

    // Se ha già un indirizzo, ritorna il veicolo così com'è
    if (vehicle.addressCurrent) {
      return of(vehicle);
    }

    // Ottieni l'indirizzo per le coordinate esistenti
    return this.getAddressFromCoordinates(vehicle.lat_current, vehicle.lon_current).pipe(
      map(address => ({
        ...vehicle,
        addressCurrent: address
      })),
      catchError(error => {
        console.warn(`Errore nel geocoding per veicolo ${vehicle.vin}:`, error);
        return of(vehicle); // Ritorna il veicolo senza indirizzo se fallisce
      })
    );
  }




  // FZ30072025 elimnata per gestione mapping VIN uid da BE
    //FZ30072025 il VIN viene usato principalmente internamente, uid è usato nel caso un cui l'utente è DEMO
    // il viene mascherato e quindi si usa l'uid del mapping della response
    getAssetInfoAsJsonString(vinAsset: string, uid?: string): string {
    if (!vinAsset && !uid) {
      return 'null';
    }

    try {
      let assetDetails;
      if (Array.isArray(this.iotAssetsOfCustomer)) {
        if (uid) {
          assetDetails = this.iotAssetsOfCustomer.find((item) => item.uid === uid);
        } else if (vinAsset) {
          assetDetails = this.iotAssetsOfCustomer.find((item) => item.vin === vinAsset);
        }
      }
      return assetDetails || null;
    } catch (error) {
      console.error(`Errore durante il recupero dei dati anagrafici per l'asset ${vinAsset} o uid ${uid}:`, error);
      return 'null';
    }
  }


  refreshVehicles(
    assetType: string,
    uidCustomer: string,
    uidAssets: string[]
  ): Observable<IotAsset[]> {
    // 🔧 SAFETY CHECK: Assicurati che la configurazione mapping sia caricata
    if (!this.configLoaded || !this.mappingConfig) {
      console.log('⏳ Configurazione mapping non pronta per refresh, attendo caricamento...');
      this.initializeService(); // Forza l'inizializzazione se non è stata fatta

      // Attendi che la configurazione sia pronta (max 5 secondi)
      return new Observable<IotAsset[]>((observer) => {
        const checkConfig = () => {
          if (this.configLoaded && this.mappingConfig) {
            console.log('✅ Configurazione mapping pronta, procedo con refreshVehicles');
            this.executeRefreshVehicles(assetType, uidCustomer, uidAssets).subscribe({
              next: (result) => observer.next(result),
              error: (error) => observer.error(error),
              complete: () => observer.complete()
            });
          } else {
            setTimeout(checkConfig, 100); // Riprova ogni 100ms
          }
        };

        // Timeout di sicurezza dopo 5 secondi
        setTimeout(() => {
          if (!this.configLoaded || !this.mappingConfig) {
            console.warn('⚠️ Timeout caricamento configurazione per refresh, procedo comunque...');
            this.executeRefreshVehicles(assetType, uidCustomer, uidAssets).subscribe({
              next: (result) => observer.next(result),
              error: (error) => observer.error(error),
              complete: () => observer.complete()
            });
          }
        }, 5000);

        checkConfig(); // Prima verifica immediata
      });
    }

    return this.executeRefreshVehicles(assetType, uidCustomer, uidAssets);
  }

  private executeRefreshVehicles(
    assetType: string,
    uidCustomer: string,
    uidAssets: string[]
  ): Observable<IotAsset[]> {
    const url = this.apiUrl + 'v1/rt/machine/data';
    const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
    const varUtc = userInfo.user?.preferences.timezone.label || 'utc';

    const body = {
      uidVehicles: uidAssets,
    };

    // Effettua la chiamata POST
    return this.http.post<IotAssetsApiResponse>(url, body).pipe(
      switchMap((response) => {
        // 🚀 NUOVO: Usa il metodo ottimizzato per processare tutti i veicoli insieme
        console.log('🔄 REFRESH: Processamento completo veicoli con indirizzi');
        return this.processVehiclesWithAddress(response.data, response.mappings);
      }),
      withLatestFrom(this.store.select((state) => state.iotAssets.iotAssets)),
      switchMap(([newVehicles, currentVehicles]) => {
        // Confronta i veicoli nuovi con quelli attuali per aggiornare solo quelli necessari
        const updated$ = newVehicles.map((vehicle) => {
          const currentVehicle = currentVehicles.find(
            (v) => v.vin === vehicle.vin
          );

          // Determina se è un nuovo veicolo o se le coordinate sono cambiate
          const isNewVehicle = !currentVehicle;
          const coordsChanged = isNewVehicle ||
                               (currentVehicle.lat_current !== vehicle.lat_current ||
                                currentVehicle.lon_current !== vehicle.lon_current);

          // Log per debug
          if (isNewVehicle) {
            console.log(`Nuovo veicolo trovato: ${vehicle.plate}`);
          } else if (coordsChanged) {
            console.log(`Coordinate cambiate per il veicolo: ${vehicle.plate}`);
          }

          // Aggiorna i dati dell'asset se è un nuovo veicolo o se le coordinate sono cambiate
          if (isNewVehicle || coordsChanged) {
            return this.updateAssetData(vehicle);
          } else {
            return of(vehicle); // Ritorna il veicolo senza modifiche
          }
        });

        return forkJoin(updated$); // Ritorna tutti i veicoli aggiornati
      }),
     //tap((vehicles) => console.log('Veicoli aggiornati con refresh:', vehicles)),
      catchError((error) => {
        console.error(
          'Errore durante la chiamata a refreshVehicles:',
          error
        );
        return throwError(
          () => new Error('Errore durante la chiamata al servizio.')
        );
      })
    );
  }

  private updateAssetData(vehicle: IotAsset, vehicleResponse?: IotAssetsApiResponse): Observable<IotAsset> {
    // Semplicemente ritorna il veicolo con l'assetInfo aggiornato se disponibile
    return of({
      ...vehicle,
      assetInfo: (vehicleResponse?.mappings?.vehicles && vehicleResponse.mappings.vehicles[1])
        ? this.getAssetInfoAsJsonString(vehicleResponse.mappings.vehicles[1], vehicleResponse.mappings.vehicles[0])
        : this.getAssetInfoAsJsonString(vehicle.vin),
    });
  }
  getDriverInfoAsJsonString(uidDriver: string): string {
    if (!uidDriver) {
      return 'null';
    }

    try {
      // Cerca il driver direttamente nell'array driversOfCustomer
      const driverDetails = Array.isArray(this.driversOfCustomer)
        ? this.driversOfCustomer.find((item) => item.uid === uidDriver)
        : undefined;

      // Converti in stringa JSON con formattazione
      return driverDetails || null;
    } catch (error) {
      console.error(`Errore durante il recupero dei dati anagrafici per il driver ${uidDriver}:`, error);
      return 'null';
    }
  }

  public getAddressFromCoordinates(lat: number, lon: number): Observable<string> {
    console.log(`🌍 Geocoding per coordinate: lat=${lat}, lon=${lon}`);
    // Aggiungiamo qui il controllo sulle coordinate
    if (lat == null || lon == null) {
      console.warn(`Coordinate mancanti: lat=${lat}, lon=${lon}`);
      return of('N/A'); // Ritorna 'N/A' se le coordinate non sono valide
    }

    // Crea cache key per evitare richieste duplicate
    const cacheKey = `${lat.toFixed(6)}_${lon.toFixed(6)}`;
    console.log(`🔑 Cache key generata: ${cacheKey}`);

    // Controlla se abbiamo già questo indirizzo in cache (opzionale - implementabile con localStorage)
    const cachedAddress = this.getAddressFromCache(cacheKey);
    console.log(`📦 Indirizzo dalla cache: ${cachedAddress ? 'Trovato' : 'Non trovato'}`) ;
    if (cachedAddress) {
      return of(cachedAddress);
    }

    // Utilizza il rate limiting per OpenStreetMap Nominatim
    return new Observable<string>((observer) => {
      this.geocodingQueue.push({ lat, lon, observer });
      this.processGeocodingQueue();
    });
  }

  private getAddressFromCache(cacheKey: string): string | null {
    try {
      const cached = localStorage.getItem(`${this.GEOCACHE_PREFIX}${cacheKey}`);
      if (cached) {
        const { address, timestamp } = JSON.parse(cached);
        // Cache valida per 24 ore
        if (Date.now() - timestamp < this.CACHE_TTL) {
          return address;
        } else {
          localStorage.removeItem(`${this.GEOCACHE_PREFIX}${cacheKey}`);
        }
      }
    } catch (error) {
      console.warn('Errore nel recupero dalla cache:', error);
    }
    return null;
  }

  private saveAddressToCache(lat: number, lon: number, address: string): void {
    try {
      // Controlla e pulisce la cache se necessario
      this.manageCacheSize();

      const cacheKey = `${lat.toFixed(6)}_${lon.toFixed(6)}`;
      const cacheData = {
        address,
        timestamp: Date.now()
      };
      localStorage.setItem(`${this.GEOCACHE_PREFIX}${cacheKey}`, JSON.stringify(cacheData));
    } catch (error: any) {
      console.warn('Errore nel salvataggio in cache:', error);
      // Se localStorage è pieno, prova a pulire la cache e riprova
      if (error?.name === 'QuotaExceededError') {
        this.clearGeocodeCache();
        try {
          const cacheKey = `${lat.toFixed(6)}_${lon.toFixed(6)}`;
          const cacheData = { address, timestamp: Date.now() };
          localStorage.setItem(`${this.GEOCACHE_PREFIX}${cacheKey}`, JSON.stringify(cacheData));
        } catch (retryError) {
          console.error('Impossibile salvare in cache anche dopo la pulizia:', retryError);
        }
      }
    }
  }

  private manageCacheSize(): void {
    try {
      // Ottieni tutte le chiavi della cache geocoding
      const geocacheKeys = Object.keys(localStorage)
        .filter(key => key.startsWith(this.GEOCACHE_PREFIX));

      if (geocacheKeys.length >= this.MAX_CACHE_ITEMS) {
        console.log(`Cache geocoding raggiunge il limite (${geocacheKeys.length}/${this.MAX_CACHE_ITEMS}), pulizia in corso...`);

        // Ordina per timestamp (più vecchi per primi)
        const cacheItems = geocacheKeys.map(key => {
          try {
            const data = JSON.parse(localStorage.getItem(key) || '{}');
            return { key, timestamp: data.timestamp || 0 };
          } catch {
            return { key, timestamp: 0 };
          }
        }).sort((a, b) => a.timestamp - b.timestamp);

        // Rimuovi il 20% degli elementi più vecchi
        const itemsToRemove = Math.floor(this.MAX_CACHE_ITEMS * 0.2);
        for (let i = 0; i < itemsToRemove && i < cacheItems.length; i++) {
          localStorage.removeItem(cacheItems[i].key);
        }

        console.log(`Rimossi ${itemsToRemove} elementi dalla cache geocoding`);
      }
    } catch (error) {
      console.warn('Errore nella gestione della dimensione cache:', error);
    }
  }

  // Metodo pubblico per pulire completamente la cache geocoding
  public clearGeocodeCache(): void {
    try {
      const geocacheKeys = Object.keys(localStorage)
        .filter(key => key.startsWith(this.GEOCACHE_PREFIX));

      geocacheKeys.forEach(key => localStorage.removeItem(key));
      console.log(`Cache geocoding pulita: rimossi ${geocacheKeys.length} elementi`);
    } catch (error) {
      console.error('Errore nella pulizia della cache geocoding:', error);
    }
  }

  // Metodo pubblico per ottenere statistiche della cache
  public getGeocacheStats(): { count: number; size: number; oldestEntry?: Date } {
    try {
      const geocacheKeys = Object.keys(localStorage)
        .filter(key => key.startsWith(this.GEOCACHE_PREFIX));

      let totalSize = 0;
      let oldestTimestamp = Date.now();

      geocacheKeys.forEach(key => {
        const item = localStorage.getItem(key);
        if (item) {
          totalSize += item.length;
          try {
            const data = JSON.parse(item);
            if (data.timestamp && data.timestamp < oldestTimestamp) {
              oldestTimestamp = data.timestamp;
            }
          } catch {}
        }
      });

      return {
        count: geocacheKeys.length,
        size: totalSize,
        oldestEntry: geocacheKeys.length > 0 ? new Date(oldestTimestamp) : undefined
      };
    } catch (error) {
      console.error('Errore nel calcolo statistiche cache:', error);
      return { count: 0, size: 0 };
    }
  }

  // Metodo per pulire automaticamente la cache scaduta
  private cleanExpiredGeocodingCache(): void {
    try {
      const geocacheKeys = Object.keys(localStorage)
        .filter(key => key.startsWith(this.GEOCACHE_PREFIX));

      let expiredCount = 0;
      const now = Date.now();

      geocacheKeys.forEach(key => {
        try {
          const item = localStorage.getItem(key);
          if (item) {
            const data = JSON.parse(item);
            if (!data.timestamp || (now - data.timestamp) > this.CACHE_TTL) {
              localStorage.removeItem(key);
              expiredCount++;
            }
          }
        } catch (error) {
          // Se c'è un errore nel parsing, rimuovi l'elemento corrotto
          localStorage.removeItem(key);
          expiredCount++;
        }
      });

      if (expiredCount > 0) {
        console.log(`Pulizia cache geocoding: rimossi ${expiredCount} elementi scaduti`);
      }
    } catch (error) {
      console.warn('Errore nella pulizia automatica della cache:', error);
    }
  }

  private processGeocodingQueue(): void {
    console.log('📬 Processamento coda geocoding, elementi in coda:', this.geocodingQueue.length);
    if (this.geocodingQueue.length === 0) return;

    const now = Date.now();
    const timeSinceLastRequest = now - this.lastGeocodingRequest;

    if (timeSinceLastRequest >= this.GEOCODING_DELAY) {
      // Possiamo fare una richiesta immediata
      this.executeGeocodingRequest();
    } else {
      // Dobbiamo aspettare
      const delay = this.GEOCODING_DELAY - timeSinceLastRequest;
      setTimeout(() => {
        this.executeGeocodingRequest();
      }, delay);
    }
  }

  private executeGeocodingRequest(): void {
   // console.log('🚀 Esecuzione richiesta di geocoding dalla coda');
    if (this.geocodingQueue.length === 0) return;

    const { lat, lon, observer } = this.geocodingQueue.shift()!;
    this.lastGeocodingRequest = Date.now();

    // OpenStreetMap Nominatim (gratuito) con rate limiting
    // Aggiungiamo parametri per ottenere un indirizzo più conciso
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=16&addressdetails=1&namedetails=0&extratags=0`;

    // Usa l'istanza di HttpClient senza interceptor
    // Nota: Il browser imposterà automaticamente lo User-Agent
    this.httpWithoutInterceptor.get<any>(url).pipe(
      map((response) => {
        if (response && response.address) {
          // Costruisci un indirizzo più conciso usando i componenti dell'indirizzo
          const addr = response.address;
          const shortAddress = this.buildShortAddress(addr);

          // Salva in cache per richieste future
          this.saveAddressToCache(lat, lon, shortAddress);
          return shortAddress;
        } else if (response && response.display_name) {
          // Fallback al display_name se address non è disponibile
          this.saveAddressToCache(lat, lon, response.display_name);
          return response.display_name;
        }
        return 'Indirizzo non trovato';
      }),
      catchError((error) => {
        console.error("Errore durante il recupero dell'indirizzo da OpenStreetMap:", error);

        // Fallback: restituisci le coordinate se il servizio non è disponibile
        const fallbackAddress = `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
        return of(fallbackAddress);
      })
    ).subscribe({
      next: (address) => {
        observer.next(address);
        observer.complete();

        // Processa il prossimo elemento nella coda
        if (this.geocodingQueue.length > 0) {
          setTimeout(() => this.processGeocodingQueue(), this.GEOCODING_DELAY);
        }
      },
      error: (error) => {
        observer.error(error);

        // Processa il prossimo elemento nella coda anche in caso di errore
        if (this.geocodingQueue.length > 0) {
          setTimeout(() => this.processGeocodingQueue(), this.GEOCODING_DELAY);
        }
      }
    });
  }

  // Nuovo metodo per costruire un indirizzo più conciso
  private buildShortAddress(address: any): string {
    const parts: string[] = [];

    // Priorità per i componenti dell'indirizzo (dal più specifico al più generale)
    if (address.house_number && address.road) {
      parts.push(`${address.road} ${address.house_number}`);
    } else if (address.road) {
      parts.push(address.road);
    } else if (address.pedestrian) {
      parts.push(address.pedestrian);
    } else if (address.footway) {
      parts.push(address.footway);
    }

    // Aggiungi la località
    if (address.village) {
      parts.push(address.village);
    } else if (address.town) {
      parts.push(address.town);
    } else if (address.city) {
      parts.push(address.city);
    } else if (address.municipality) {
      parts.push(address.municipality);
    }

    // Aggiungi la provincia (opzionale)
    if (address.state && parts.length < 3) {
      parts.push(address.state);
    }

    // Se non abbiamo abbastanza informazioni, aggiungi il paese
    if (parts.length === 0 && address.country) {
      parts.push(address.country);
    }

    // Limita a massimo 3 componenti per mantenere l'indirizzo conciso
    const shortAddress = parts.slice(0, 3).join(', ');

    // Se l'indirizzo è ancora vuoto, prova con display_name troncato
    if (!shortAddress && address.display_name) {
      const displayParts = address.display_name.split(', ');
      return displayParts.slice(0, 2).join(', ');
    }

    return shortAddress || 'Indirizzo non disponibile';
  }

  private handleError(error: HttpErrorResponse) {
    console.error('Errore nel servizio:', error);
    return throwError(
      () => new Error('Errore nel caricamento dei dati dei veicoli.')
    );
  }

  // FZ ho crato alll'interno della response un campo jsonRow che contiene l'intera riga originale
  // e un campo unmappedFields che contiene i campi non mappati come array di chiave-valore
  // in modo da poterli visualizzare in un secondo momento se necessario

  getCurrentVehicleStatus(

    uidAssets: string[]
  ): Observable<IotAsset[]> {
    // 🔧 SAFETY CHECK: Assicurati che la configurazione mapping sia caricata
    if (!this.configLoaded || !this.mappingConfig) {
      console.log('⏳ Configurazione mapping non pronta, attendo caricamento...');
      this.initializeService(); // Forza l'inizializzazione se non è stata fatta

      // Attendi che la configurazione sia pronta (max 5 secondi)
      return new Observable<IotAsset[]>((observer) => {
        const checkConfig = () => {
          if (this.configLoaded && this.mappingConfig) {
            console.log('✅ Configurazione mapping pronta, procedo con getCurrentVehicleStatus');
            this.executeGetCurrentVehicleStatus(uidAssets).subscribe({
              next: (result) => observer.next(result),
              error: (error) => observer.error(error),
              complete: () => observer.complete()
            });
          } else {
            setTimeout(checkConfig, 100); // Riprova ogni 100ms
          }
        };

        // Timeout di sicurezza dopo 5 secondi
        setTimeout(() => {
          if (!this.configLoaded || !this.mappingConfig) {
            console.warn('⚠️ Timeout caricamento configurazione, procedo comunque...');
            this.executeGetCurrentVehicleStatus(uidAssets).subscribe({
              next: (result) => observer.next(result),
              error: (error) => observer.error(error),
              complete: () => observer.complete()
            });
          }
        }, 5000);

        checkConfig(); // Prima verifica immediata
      });
    }

    return this.executeGetCurrentVehicleStatus(uidAssets);
  }

  private executeGetCurrentVehicleStatus(uidAssets: string[]): Observable<IotAsset[]> {
    const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
    const varUtc = userInfo.user?.preferences.timezone.label || 'utc';
    const url = this.apiUrl + 'v1/rt/machine/data';

    const body = {
      uidVehicles: uidAssets,
    };

    // Effettua la chiamata POST
    return this.http.post<IotAssetsApiResponse>(url, body).pipe(
      switchMap((response) => {
        // 🚀 NUOVO: Usa il metodo ottimizzato per processare tutti i veicoli insieme
        console.log('🏭 SERVIZIO: Processamento completo veicoli con indirizzi');
        return this.processVehiclesWithAddress(response.data, response.mappings);
      }),
      switchMap((vehicles) => {
        // Aggiorna i dati degli asset
        const updated$ = vehicles.map((vehicle) =>
          this.updateAssetData(vehicle)
        );
        return forkJoin(updated$);
      }),
      catchError((error) => {
        console.error(
          'Errore durante la chiamata a getCurrentVehicleStatus:',
          error
        );
        return throwError(
          () => new Error('Errore durante la chiamata al servizio.')
        );
      })
    );
  }

  // FZ correzzione funzione per NOT CONNECTED già da mezz'ora
  //#FZ 22072025
// Correzione della funzione checkAssetConnection
private checkAssetConnection(statusEnteredAt: string, originalStatus: string): 'MOVING' | 'PARKED' | 'IDLE' | 'UNKNOWN' | 'NOTCONNECTED' {
  if (!statusEnteredAt) return 'UNKNOWN';

  // Calcola la differenza tra ora e l'ultimo stato registrato
  const lastStatusTime = new Date(statusEnteredAt).getTime();
  const currentTime = new Date().getTime();
  const hoursDiff = (currentTime - lastStatusTime) / (1000 * 60 * 60); // Differenza in ore

  // Se sono passate più di 24 ore, imposta lo stato a NOTCONNECTED
  if (hoursDiff > 24) {
    return 'NOTCONNECTED';
  }

  return originalStatus as 'MOVING' | 'PARKED' | 'IDLE' | 'UNKNOWN'; // Mantieni lo stato originale
}

private getSecondsDiffFromNow(inputDate: string | Date): number {
  const date = new Date(inputDate).getTime();
  const now = new Date().getTime();
  return Math.floor((now - date) / 1000);
}

// FUNZIONE NON UTILIZZATA - COMMENTATA
/**
 * Ottiene il catalogo dei segnali per un asset specifico
 * @param assetUids Array di UID degli asset per cui ottenere il catalogo dei segnali
 * @returns Observable con la risposta del catalogo dei segnali
 */
// getSignalsCatalog(assetUids: string[]): Observable<SignalCatalogResponse> {
//   const url = this.apiUrl + 'v1/rt/functions/execute';

//   const body = {
//     function: 'ohw_iot.get_signals_catalog',
//     params: [assetUids]
//   };

//   return this.http.post<SignalCatalogResponse>(url, body).pipe(
//     tap((response) => console.log('Catalogo segnali ricevuto:', response)),
//     catchError((error) => {
//       console.error('Errore durante la chiamata a getSignalsCatalog:', error);
//       return throwError(
//         () => new Error('Errore durante il recupero del catalogo dei segnali.')
//       );
//     })
//   );
// }

// Additional Signals Configuration CRUD operations

private baseConfigEndpoint = 'v1/rt/config/general';
private baseConfigEndpointGet = 'v1/rt/config/general';


/**
 * Ottiene la configurazione journey dall'API
 */
getJourneyConfigFromAPI(uidCustomer: string, uidUser: string): Observable<MappingConfig[]> {
  const cacheKey = `journey_${uidCustomer}`;
  const now = Date.now();

  // Controlla se esiste in cache e se è ancora valida
  const cached = this.apiConfigCache.get(cacheKey);
  if (cached && (now - cached.timestamp) < this.API_CACHE_TTL) {
    //console.log('✅ getJourneyConfigFromAPI: Usando configurazione dalla cache per customer:', uidCustomer);
    return of(cached.data);
  }

  //console.log('🌐 getJourneyConfigFromAPI: Chiamata API per customer:', uidCustomer);
  const queryString = `?scope=${AdditionalSignalsScope.JOURNEY_CONFIG}&uidUser=${uidUser}&uidCustomer=${uidCustomer}`;

  return this.http.get(this.apiUrl + this.baseConfigEndpointGet + queryString).pipe(
    map((res: any) => {
      let data: MappingConfig[] = res.data || [];

      // Salva in cache per chiamate future
      this.apiConfigCache.set(cacheKey, {
        data: data,
        timestamp: now
      });

     // console.log('✅ getJourneyConfigFromAPI: Configurazione journey caricata:', data.length, 'elementi');
      return data;
    }),
    catchError((error) => {
      console.error('Errore durante il recupero della configurazione journey:', error);
      return throwError(
        () => new Error('Errore durante il recupero della configurazione di mapping.')
      );
    })
  );
}

/**
 * Ottiene la configurazione catalog (Signals Catalog) dall'API
 */
getCatalogConfigFromAPI(uidCustomer: string, uidUser: string, audience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER'): Observable<MappingConfig[]> {
  const cacheKey = `catalog_${uidCustomer}_${audience}`;
  const now = Date.now();

  // Controlla se esiste in cache e se è ancora valida
  const cached = this.apiConfigCache.get(cacheKey);
  if (cached && (now - cached.timestamp) < this.API_CACHE_TTL) {
    console.log('✅ getCatalogConfigFromAPI: Usando configurazione dalla cache per customer:', uidCustomer, 'audience:', audience);
    return of(cached.data);
  }

  console.log('🌐 getCatalogConfigFromAPI: Chiamata API per customer:', uidCustomer, 'audience:', audience);
  const queryString = `?scope=${AdditionalSignalsScope.CATALOG_CONFIG}&uidUser=${uidUser}&uidCustomer=${uidCustomer}&audience=${audience}`;

  return this.http.get(this.apiUrl + this.baseConfigEndpointGet + queryString).pipe(
    map((res: any) => {
      let data: MappingConfig[] = res.data || [];

      // Salva in cache per chiamate future
      this.apiConfigCache.set(cacheKey, {
        data: data,
        timestamp: now
      });

      console.log('✅ getCatalogConfigFromAPI: Configurazione catalog caricata:', data.length, 'elementi');
      return data;
    }),
    catchError((error) => {
      console.error('Errore durante il recupero della configurazione catalog:', error);
      return of([]);
    })
  );
}

/**
 * Ottiene la configurazione dashboard dall'API
 */
getDashboardConfigFromAPI(uidCustomer: string, uidUser: string): Observable<MappingConfig[]> {
  const cacheKey = `dashboard_${uidCustomer}`;
  const now = Date.now();

  // Controlla se esiste in cache e se è ancora valida
  const cached = this.apiConfigCache.get(cacheKey);
  if (cached && (now - cached.timestamp) < this.API_CACHE_TTL) {
    console.log('✅ getDashboardConfigFromAPI: Usando configurazione dalla cache per customer:', uidCustomer);
    return of(cached.data);
  }

  console.log('🌐 getDashboardConfigFromAPI: Chiamata API per customer:', uidCustomer);
  const queryString = `?scope=${AdditionalSignalsScope.DASHBOARD_CONFIG}&uidUser=${uidUser}&uidCustomer=${uidCustomer}`;

  return this.http.get(this.apiUrl + this.baseConfigEndpointGet + queryString).pipe(
    map((res: any) => {
      let data: MappingConfig[] = res.data || [];

      // Salva in cache per chiamate future
      this.apiConfigCache.set(cacheKey, {
        data: data,
        timestamp: now
      });

      console.log('✅ getDashboardConfigFromAPI: Configurazione dashboard caricata:', data.length, 'elementi');
      return data;
    }),
    catchError((error) => {
      console.error('Errore durante il recupero della configurazione dashboard:', error);
      return throwError(
        () => new Error('Errore durante il recupero della configurazione dashboard.')
      );
    })
  );
}

/**
 * Crea una nuova configurazione di mapping
 * @param config Configurazione da creare
 * @returns Observable con la risposta della creazione
 */
createMappingConfig(config: MappingConfig): Observable<any> {
  return this.http.post(this.apiUrl + this.baseConfigEndpoint, config).pipe(
    tap((response) => console.log('Configurazione mapping creata:', response)),
    catchError((error) => {
      console.error('Errore durante la creazione della configurazione mapping:', error);
      return throwError(
        () => new Error('Errore durante la creazione della configurazione di mapping.')
      );
    })
  );
}

/**
 * Modifica una configurazione di mapping esistente
 * @param config Configurazione da modificare
 * @returns Observable con la risposta della modifica
 */
editMappingConfig(config: MappingConfig): Observable<any> {
  return this.http.put(this.apiUrl + this.baseConfigEndpoint, config).pipe(
    tap((response) => console.log('Configurazione mapping modificata:', response)),
    catchError((error) => {
      console.error('Errore durante la modifica della configurazione mapping:', error);
      return throwError(
        () => new Error('Errore durante la modifica della configurazione di mapping.')
      );
    })
  );
}
// FUNZIONE NON UTILIZZATA - COMMENTATA
// getAdditionalSignalsConfig(uidCustomer: string, userId: string): Observable<AdditionalSignalsConfig[]> {
//   const queryString = `?scope=${AdditionalSignalsScope.ADDITIONAL_SIGNALS}&uidUser=${userId}&uidCustomer=${uidCustomer}`;
//   return this.http.get(this.apiUrl + this.baseConfigEndpoint + queryString).pipe(
//     map((res: any) => {
//       let data: AdditionalSignalsConfig[] = res.data;
//       let configs: AdditionalSignalsConfig[] = res.data;
//       // Eventuali filtri aggiuntivi possono essere applicati qui
//       data.map(config => {
//         // Logica di filtraggio se necessaria in futuro
//       });
//       return configs;
//     }),
//     catchError((error) => {
//       console.error('Errore durante il recupero della configurazione additional signals:', error);
//       return throwError(
//         () => new Error('Errore durante il recupero della configurazione degli additional signals.')
//       );
//     })
//   );
// }

// FUNZIONE NON UTILIZZATA - COMMENTATA
/**
 * Crea una nuova configurazione additional signals
 * @param config Configurazione da creare
 * @returns Observable con la risposta della creazione
 */
// createAdditionalSignalsConfig(config: AdditionalSignalsConfig): Observable<any> {
//   return this.http.post(this.apiUrl + this.baseConfigEndpoint, config).pipe(
//     tap((response) => console.log('Configurazione additional signals creata:', response)),
//     catchError((error) => {
//       console.error('Errore durante la creazione della configurazione additional signals:', error);
//       return throwError(
//         () => new Error('Errore durante la creazione della configurazione degli additional signals.')
//       );
//     })
//   );
// }

/**
 * Modifica una configurazione additional signals esistente
 * @param config Configurazione da modificare
 * @returns Observable con la risposta della modifica
 */
editAdditionalSignalsConfig(config: AdditionalSignalsConfig): Observable<any> {
  return this.http.put(this.apiUrl + this.baseConfigEndpoint, config).pipe(
    tap((response) => console.log('Configurazione additional signals modificata:', response)),
    catchError((error) => {
      console.error('Errore durante la modifica della configurazione additional signals:', error);
      return throwError(
        () => new Error('Errore durante la modifica della configurazione degli additional signals.')
      );
    })
  );
}

/**
 * Elimina una configurazione additional signals
 * @param configId ID della configurazione da eliminare
 * @returns Observable con la risposta dell'eliminazione
 */
deleteAdditionalSignalsConfig(configId: string): Observable<any> {
  return this.http.delete(this.apiUrl + this.baseConfigEndpoint + `?id=${configId}`).pipe(
    tap((response) => console.log('Configurazione additional signals eliminata:', response)),
    catchError((error) => {
      console.error('Errore durante l\'eliminazione della configurazione additional signals:', error);
      return throwError(
        () => new Error('Errore durante l\'eliminazione della configurazione degli additional signals.')
      );
    })
  );
}

/**
 * Ottiene il catalogo per un cliente specifico
 * @param uidCustomer UID del cliente
 * @returns Observable con la risposta del catalogo
 */
getCatalog(): Observable<CatalogResponse> {
    return this.storeAnag.pipe(
      select(getApplySelectedCustomer),
      switchMap((selectedCustomer) => {
        const uidCustomer = selectedCustomer?.uid;

        if (!uidCustomer) {
          console.error('❌ Impossibile recuperare catalogo: customer non selezionato');
          return throwError(() => new Error('Customer non selezionato per il recupero del catalogo'));
        }

        console.log('📡 getCatalog() - Chiamata API al catalogo per customer:', uidCustomer);
        const url = `${this.apiUrl}v1/rt/catalog?uidCustomer=${uidCustomer}`;

        return this.http.get<CatalogResponse>(url).pipe(
          tap((response) => console.log('✅ Catalogo ricevuto per customer:', uidCustomer)),
          catchError((error) => {
            console.error('Errore durante la chiamata a getCatalog:', error);
            return throwError(
              () => new Error('Errore durante il recupero del catalogo.')
            );
          })
        );
      }),
      takeUntil(this.destroy$)
    );
}

  // ── UControl Signals Config CRUD ─────────────────────────────────────────────
  // Endpoint: v1/rt/config/general — sempre POST.
  // Se il body include `id` il BE aggiorna, altrimenti crea (upsert lato BE).

  getUControlConfig(uidCustomer: string, uidUser: string, modelUid: string): Observable<UControlSignalsConfig[]> {
    const scope = `${AdditionalSignalsScope.UCONTROL_SIGNALS_CONFIG}-${modelUid}`;
    const queryString = `?scope=${scope}&uidUser=${uidUser}&uidCustomer=${uidCustomer}&audience=CUSTOMER`;
    return this.http.get(this.apiUrl + this.baseConfigEndpointGet + queryString).pipe(
      map((res: any) => {
        if (res?.result === 'GENERAL_ERROR') {
          console.warn('getUControlConfig: GENERAL_ERROR dal backend — trattato come lista vuota');
          return [];
        }
        return res?.data || [];
      }),
      catchError((error) => {
        console.error('Errore recupero config UControl:', error);
        return throwError(() => new Error('Errore durante il recupero della configurazione UControl.'));
      })
    );
  }

  // ── Storico scritture segnali (TELEOHW-926, piano "Storico scritture segnali Industry 4.0") ──
  // Chiamato SUBITO DOPO che il comando è stato inviato al device (es. DiagLight, invariato): questo
  // endpoint registra solo lo storico, non inoltra alcun comando. Fallimento qui non deve mai
  // bloccare il flusso di invio già completato — il chiamante decide come gestire l'errore (es. solo
  // un log in console), mai un retry automatico del comando stesso.
  appendSignalWriteHistory(
    uidVehicle: string,
    body: {
      requestId: string;
      uidCustomer: string;
      uidUser: string;
      /** 'DIAG_LIGHT' (invio reale) o 'DIAG_LIGHT_SIM' (fallback simulato, nessuna sessione RP1210 reale — test end-to-end senza device, 2026-10-24). */
      source: string;
      parameters: { signalCode: string; signalLabel?: string; valueType: string; newValue: string }[];
    }
  ): Observable<any> {
    return this.http.post(`${this.apiUrl}${VEHICLE_SIGNALS_ENDPOINT}${uidVehicle}/writes`, body).pipe(
      catchError((error) => {
        console.error('[VehicleSignals] Errore registrazione storico scritture (comando già inviato):', error);
        return of(null);
      })
    );
  }

  getMonitoringConfig(uidCustomer: string, uidUser: string, vehicleUid: string): Observable<UControlSignalsConfig[]> {
    const scope = `ucontrol-monitoring-config-${vehicleUid}`;
    const queryString = `?scope=${scope}&uidUser=${uidUser}&uidCustomer=${uidCustomer}&audience=USER`;
    return this.http.get(this.apiUrl + this.baseConfigEndpointGet + queryString).pipe(
      map((res: any) => {
        if (res?.result === 'GENERAL_ERROR') return [];
        return res?.data || [];
      }),
      catchError(() => of([]))
    );
  }

  saveUControlConfig(config: UControlSignalsConfig): Observable<any> {
    return this.http.post(this.apiUrl + this.baseConfigEndpoint, config).pipe(
      tap((response) => console.log('Config UControl salvata:', response)),
      catchError((error) => {
        console.error('Errore salvataggio config UControl:', error);
        return throwError(() => new Error('Errore durante il salvataggio della configurazione UControl.'));
      })
    );
  }

  deleteUControlConfig(configId: string): Observable<any> {
    return this.http.delete(this.apiUrl + 'v1/rt/config' + `?id=${configId}`).pipe(
      tap((response) => console.log('Config UControl eliminata:', response)),
      catchError((error) => {
        console.error('Errore eliminazione config UControl:', error);
        return throwError(() => new Error('Errore durante l\'eliminazione della configurazione UControl.'));
      })
    );
  }

  /**
   * Pulizia quando il servizio viene distrutto
   */
  ngOnDestroy(): void {
    // Notifica la distruzione del servizio
    this.destroy$.next();
    this.destroy$.complete();

    // Notifica il cambio customer per cancellare operazioni in corso
    this.customerChange$.next();
    this.customerChange$.complete();

    // Pulisci le richieste catalog in corso
    this.catalogConfigRequestsInProgress.clear();

    console.log('🧹 IotAssetsService distrutto, tutte le subscription cancellate');
  }

}
